import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../environment";
import { getSupabaseRealtime, getSupabaseRealtimeCRM } from "../../supabaseRealtime";
import { Contact, Message, Stage } from "../../types/chat";
import usePwaMobile from "./usePwaMobile";
import "./ChatMobile.css";

/* ---------- ícones (SVG line, estilo iOS) ---------- */
const Icon = {
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </svg>
  ),
  back: (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  ),
  person: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="9" r="3.6" />
      <path d="M5 19a7 7 0 0 1 14 0z" />
    </svg>
  ),
  tickRead: (
    <svg width="16" height="11" viewBox="0 0 18 12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 6.5l3 3 6.5-7" />
      <path d="M7 9.5l1 -1M9.5 6.5L15 0" />
    </svg>
  ),
  send: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 11L21 3l-8 18-2.2-7L3 11z" />
    </svg>
  ),
  mic: (
    <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 14a3.2 3.2 0 0 0 3.2-3.2V5.2a3.2 3.2 0 1 0-6.4 0v5.6A3.2 3.2 0 0 0 12 14z" />
      <path d="M18 10.8a.9.9 0 0 0-1.8 0 4.2 4.2 0 0 1-8.4 0 .9.9 0 0 0-1.8 0 6 6 0 0 0 5.1 5.93V19H9.6a.9.9 0 0 0 0 1.8h4.8a.9.9 0 0 0 0-1.8h-1.5v-2.27A6 6 0 0 0 18 10.8z" />
    </svg>
  ),
  trash: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M10 7V5h4v2M6 7l1 13h10l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  pause: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <rect x="7" y="5" width="3.4" height="14" rx="1.2" />
      <rect x="13.6" y="5" width="3.4" height="14" rx="1.2" />
    </svg>
  ),
  play: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.6v12.8a.8.8 0 0 0 1.22.68l10-6.4a.8.8 0 0 0 0-1.36l-10-6.4A.8.8 0 0 0 8 5.6z" />
    </svg>
  ),
  dots: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="12" cy="19" r="1.9" />
    </svg>
  ),
  close: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  card: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 10h18M7 15h5" />
    </svg>
  ),
  funnel: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h18l-7 8v6l-4-2v-4z" />
    </svg>
  ),
  robot: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="11" rx="2.5" />
      <path d="M12 4.5v3.5M2 13v2M22 13v2" />
      <circle cx="9.2" cy="13.2" r="1.15" fill="currentColor" stroke="none" />
      <circle cx="14.8" cy="13.2" r="1.15" fill="currentColor" stroke="none" />
    </svg>
  ),
  gear: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 13a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-2.7-1.1l-.1.1A2 2 0 1 1 2.7 17l.1-.1A1.6 1.6 0 0 0 1.7 14H1.6a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 3.4 7.3l-.1-.1A2 2 0 1 1 6.1 4.4l.1.1A1.6 1.6 0 0 0 8 4.8V4.6a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8 1.6 1.6 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.4.9z" />
    </svg>
  ),
  trophy: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 4h2a1 1 0 0 1 1 1c0 2.6-1.6 4.6-4 5v.2A6 6 0 0 1 13 16v2h2a1 1 0 0 1 1 1v1H8v-1a1 1 0 0 1 1-1h2v-2a6 6 0 0 1-4-5.8V5a1 1 0 0 1 1-1h10zm0 2v3.5c1.2-.4 2-1.4 2-2.7V6h-2zM6 6H4v.8c0 1.3.8 2.3 2 2.7V6z" />
    </svg>
  ),
  template: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  ),
  download: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" />
      <path d="M5 19h14" />
    </svg>
  ),
  clip: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 0 1-7.78-7.78l9.2-9.19a3.67 3.67 0 0 1 5.18 5.19l-9.2 9.18a1.83 1.83 0 0 1-2.6-2.6l8.49-8.48" />
    </svg>
  ),
};

const AV_COLORS = ["#0a7e94", "#6b8e23", "#9333ea", "#c2410c", "#0e7490", "#be123c", "#1d4ed8", "#0b6b58"];

function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

// paleta de etiquetas de etapa (cor consistente por nome de etapa)
const STAGE_COLORS = [
  { bg: "#d7f3e8", fg: "#0a7d62" },
  { bg: "#e0ecfd", fg: "#1d4ed8" },
  { bg: "#fde8d7", fg: "#b45309" },
  { bg: "#f3e0fb", fg: "#7e22ce" },
  { bg: "#fde0e6", fg: "#be123c" },
  { bg: "#e2f0d0", fg: "#4d7c0f" },
  { bg: "#d9f1f4", fg: "#0e7490" },
  { bg: "#fef3c7", fg: "#a16207" },
];
function corEtapa(nome: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < nome.length; i++) h = (h * 31 + nome.charCodeAt(i)) >>> 0;
  return STAGE_COLORS[h % STAGE_COLORS.length];
}
function initials(nome: string): string {
  const parts = (nome || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function toDate(ts?: number | string): Date | null {
  if (!ts && ts !== 0) return null;
  const d = new Date(typeof ts === "string" && /^\d+$/.test(ts) ? Number(ts) : ts);
  return isNaN(d.getTime()) ? null : d;
}
function formatHora(ts?: number | string): string {
  const d = toDate(ts);
  if (!d) return typeof ts === "string" ? ts : "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dayLabel(ts?: number | string): string {
  const d = toDate(ts);
  if (!d) return "";
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);
  if (sameDay(d, hoje)) return "Hoje";
  if (sameDay(d, ontem)) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
/* Áudio só vira player quando a mensagem carrega uma URL tocável. O banco às vezes
   guarda base64/transcrição no campo `mensagem`; nesse caso mantém a tag antiga. */
function isAudioPlayable(m: Message): boolean {
  const t = (m.type || "").toLowerCase();
  if (t !== "audio") return false;
  const src = (m.mensagem || "").trim();
  return /^(https?:\/\/|blob:|data:audio\/|\/storage\/v1\/object\/public\/)/i.test(src);
}
function mediaLabel(type: string): string | null {
  const t = (type || "").toLowerCase();
  if (t === "imagem" || t === "image") return "🖼️ Imagem";
  if (t === "video") return "🎥 Vídeo";
  if (t === "audio") return "🎙️ Áudio";
  if (t === "document") return "📄 Documento";
  if (t === "contact") return "👤 Contato";
  return null;
}

/* ---------- mídia no balão (porte do chat.tsx desktop) ----------
   O banco guarda em `mensagem` ora a URL pública do storage, ora base64 cru
   (mídia antiga/Evolution). Os dois casos viram conteúdo renderizável; só o que
   não é nenhum dos dois cai na tag textual antiga ("🖼️ Imagem"). */
const URL_MIDIA_RE = /^(https?:\/\/|blob:|data:|\/storage\/v1\/object\/public\/)/i;
function ehUrlMidia(s?: string | null): boolean {
  return URL_MIDIA_RE.test((s || "").trim());
}
type MidiaKind = "image" | "video" | "audio" | "document";
/* Tipo efetivo da mídia: usa o `type` da mensagem e, quando ele não bate (mídia
   recebida costuma vir com type genérico), infere pela pasta/extensão da URL —
   mesma heurística do previewUltimaMsg() do desktop. */
function tipoMidia(m: Message): MidiaKind | null {
  const t = (m.type || "").toLowerCase();
  if (t === "image" || t === "imagem" || t === "sticker") return "image";
  if (t === "video") return "video";
  if (t === "audio" || t === "ptt") return "audio";
  if (t === "document" || t === "documento") return "document";
  const s = (m.mensagem || "").trim();
  if (!ehUrlMidia(s)) return null;
  if (/^data:image\//i.test(s) || /\/imagem\/|\.(jpe?g|png|webp|gif|bmp)(\?|$)/i.test(s)) return "image";
  if (/^data:video\//i.test(s) || /\/video\/|\.(mp4|3gp|mov|webm|mkv)(\?|$)/i.test(s)) return "video";
  if (/^data:audio\//i.test(s) || /\/audio\/|\.(ogg|opus|mp3|m4a|aac|amr|wav)(\?|$)/i.test(s)) return "audio";
  if (/\/document\/|\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar)(\?|$)/i.test(s)) return "document";
  return null;
}
/* src pronto pro <img>/<video>/<a>: URL passa direto, base64 cru vira data URI
   com o mime do tipo (igual ao desktop). */
function srcMidia(m: Message, kind: MidiaKind): string {
  const s = (m.mensagem || "").trim();
  if (ehUrlMidia(s)) return s;
  const mime =
    kind === "image" ? "image/png" : kind === "video" ? "video/mp4" : kind === "audio" ? "audio/mp3" : "application/octet-stream";
  return `data:${mime};base64,${s}`;
}
/* Só renderiza mídia quando há conteúdo de fato (URL ou base64). Transcrição de
   áudio e texto solto com type errado continuam caindo no caminho de texto. */
function midiaRenderizavel(m: Message): MidiaKind | null {
  const kind = tipoMidia(m);
  if (!kind) return null;
  const s = (m.mensagem || "").trim();
  if (!s) return null;
  if (ehUrlMidia(s)) return kind;
  // base64 cru: heurística conservadora (string longa sem espaço)
  return s.length > 200 && !/\s/.test(s) ? kind : null;
}
function nomeArquivo(m: Message): string {
  const doc = (m.doc_name || "").trim();
  if (doc) return doc;
  const s = (m.mensagem || "").trim();
  if (!ehUrlMidia(s) || /^data:/i.test(s)) return "documento";
  return decodeURIComponent((s.split("/").pop() || "documento").replace(/\?.*$/, ""));
}
const FILE_COLORS: Record<string, string> = {
  pdf: "#e2574c",
  doc: "#2b7cd3", docx: "#2b7cd3",
  xls: "#1d9d5d", xlsx: "#1d9d5d", csv: "#1d9d5d",
  ppt: "#d35230", pptx: "#d35230",
  zip: "#f0ad4e", rar: "#f0ad4e", "7z": "#f0ad4e",
  txt: "#607d8b",
};
/* Ícone de arquivo (página + faixa com a extensão) — mesmo desenho do desktop,
   sem o HEAD de Content-Length: no celular é requisição extra em rede móvel e o
   tamanho não cabe no balão estreito. */
const FileIcon = ({ ext }: { ext: string }) => {
  const cor = FILE_COLORS[ext] || "#007e94";
  const label = (ext || "FILE").toUpperCase().slice(0, 4);
  return (
    <svg width="38" height="38" viewBox="0 0 40 40" style={{ flex: "0 0 auto", display: "block" }}>
      <path d="M10 4 H26 L32 10 V34 A2 2 0 0 1 30 36 H10 A2 2 0 0 1 8 34 V6 A2 2 0 0 1 10 4 Z" fill="#fff" stroke="#d9dee3" strokeWidth="1.4" />
      <path d="M26 4 V8 A2 2 0 0 0 28 10 H32 Z" fill="#eef1f4" stroke="#d9dee3" strokeWidth="1" />
      <rect x="6" y="20" width="26" height="12" rx="2.5" fill={cor} />
      <text x="19" y="29" fontSize={label.length > 3 ? 7 : 8.5} fontWeight="700" fill="#fff" textAnchor="middle" fontFamily="system-ui, Arial, sans-serif">
        {label}
      </text>
    </svg>
  );
};

/* Idioma do lead. O CRM traduz a conversa do lead estrangeiro pro operador ler
   em português; em lead brasileiro o bloco "Tradução" só repetia a mensagem. Sem
   idioma definido o desktop assume PT-BR — aqui também. */
function ehPortugues(idioma?: string | null): boolean {
  const s = (idioma || "").trim().toLowerCase().replace(/[\s_]+/g, "-");
  if (!s) return true;
  return ["pt", "pt-br", "ptbr", "pt-pt", "portugues", "português", "portuguese", "brasileiro"].includes(s);
}

/* Só os dígitos do telefone (espelha normalizePhone do chat.tsx desktop). Usado
   para casar o fone que vem do realtime do CRM com o fone do contato, que pode
   chegar formatado/diferente entre os dois bancos. */
function soDigitos(fone?: number | string | null): string {
  if (fone == null) return "";
  return String(fone).replace(/\D/g, "");
}

/* 554896381284 -> (48) 9638-1284 (espelha formatNumeroWhats do desktop) */
function formatFone(fone?: number | string | null): string {
  if (fone == null) return "";
  let d = String(fone).replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("55") && d.length > 11) d = d.slice(2);
  if (/^800\d{7}$/.test(d)) return `0800 ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
}

/* Janela de 24h da Meta (margem de 1h, igual ao desktop): só uma mensagem
   RECEBIDA do lead reabre a janela — template que NÓS enviamos não reabre. */
const WINDOW_HOURS = 23;
const TEMPLATE_TIME_IDS = ["146", "159"]; // times liberados historicamente, mesmo sem nº oficial detectado
function foraDaJanela24h(c?: Contact | null): boolean {
  if (!c) return false;
  const times = (c.messages || [])
    .filter((m) => m.status === "received")
    .map((m) => new Date(m.timestamp).getTime())
    .filter((t) => isFinite(t));
  if (!times.length) return true; // nunca recebeu nada do lead
  return Date.now() - Math.max(...times) > WINDOW_HOURS * 60 * 60 * 1000;
}

interface ChatTemplate {
  name: string;
  language: string;
  body: string;
  headerFormat: string;
  headerImage: string;
  vars: string[];
  botoes?: { type?: string; text?: string; url?: string; phone_number?: string }[];
}

type Tab = "todos" | "nao";

interface Funnel {
  nome_funil: string;
}
interface CardField {
  id: number;
  id_grupo: number;
  label: string;
  tipo: string;
  opcoes?: string;
  obrigatorio?: boolean;
  ordem?: number;
}
interface CardGroup {
  id: number;
  nome: string;
  ordem?: number;
  padrao?: boolean;
  fields: CardField[];
}
interface CardFunnel {
  id: number;
  nome: string;
}
interface RespostaRapida {
  id: number;
  titulo: string;
  texto: string;
}

const TIME_159 = "159";

const ChatMobile: React.FC = () => {
  const navigate = useNavigate();
  usePwaMobile(); // manifest + ícone ConversAI + service worker (PWA instalável)
  /* Trava o rubber-band do body no iOS (arrasto no header arrastava a página
     inteira e deixava vão branco). Classe some se o componente desmontar. */
  useEffect(() => {
    document.documentElement.classList.add("cmob-lock");
    document.body.classList.add("cmob-lock");
    return () => {
      document.documentElement.classList.remove("cmob-lock");
      document.body.classList.remove("cmob-lock");
    };
  }, []);
  /* Contexto de sessão CONGELADO no mount (useState lazy roda 1x).
     O localStorage é compartilhado entre todas as abas do domínio: reler a cada
     render fazia a lista trocar de tenant no meio do uso quando outra aba/tela
     mexia nas chaves (flagrado em produção: id_cliente 164->150 + expulsão pro
     login). A sessão do mobile agora só muda com reload. */
  const [sessao] = useState(() => {
    const ok = typeof window !== "undefined";
    return {
      idTime: ok ? localStorage.getItem("id_cliente") : null,
      idUser: ok ? localStorage.getItem("id_user") : null,
      idGrupo: ok ? localStorage.getItem("id_grupo") : null,
      isAdminMaster: ok && localStorage.getItem("isAdminMaster") === "true",
      logado: ok && !!localStorage.getItem("id_user") && !!localStorage.getItem("id_cliente"),
    };
  });
  const { idTime, idUser, idGrupo, isAdminMaster, logado } = sessao;
  // Guarda de autenticação: acessar /mobile-app sem login -> manda pro login mobile.
  useEffect(() => {
    if (!logado) navigate("/mobile-app/login", { replace: true });
  }, [logado, navigate]);
  const [instanceName, setInstanceName] = useState<string | null>(
    typeof window !== "undefined" ? localStorage.getItem("instanceName") : null
  );

  const [contatos, setContatos] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<Tab>("todos");
  // funil fixo (config): já inicia o filtro selecionado nele
  const [filtroFunil, setFiltroFunil] = useState(
    typeof window !== "undefined" ? localStorage.getItem("cmob_funil_fixo") || "" : ""
  );
  const [filtroEtapa, setFiltroEtapa] = useState(""); // "" = todas

  // ---- Configurações (persistidas no localStorage do dispositivo) ----
  const [configOpen, setConfigOpen] = useState(false);
  const [funilFixo, setFunilFixo] = useState(
    typeof window !== "undefined" ? localStorage.getItem("cmob_funil_fixo") || "" : ""
  );
  const [etapaMeta, setEtapaMeta] = useState(
    typeof window !== "undefined" ? localStorage.getItem("cmob_etapa_meta") || "" : ""
  );
  const [etiquetasCores, setEtiquetasCores] = useState(
    typeof window !== "undefined" ? localStorage.getItem("cmob_etiquetas_cores") === "1" : false
  );
  const [pushMeta, setPushMeta] = useState(
    typeof window !== "undefined" ? localStorage.getItem("cmob_push_meta") === "1" : false
  );
  // Push real (Story 9): assinatura Web Push deste aparelho no serviço cmob-push
  const [pushOn, setPushOn] = useState(
    typeof window !== "undefined" ? localStorage.getItem("cmob_push_on") === "1" : false
  );
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  // Sair: 2 toques (confirmação inline, sem confirm() nativo)
  const [sairConfirm, setSairConfirm] = useState(false);
  const sairTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [view, setView] = useState<"list" | "chat">("list");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [aviso, setAviso] = useState("");
  const [enviando, setEnviando] = useState(false);

  // ---- Ações (atendimento humano / funil / etapa / cards) ----
  const [stages, setStages] = useState<Stage[]>([]);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  // Admin master: funis/etapas de TODOS os times do grupo. Escolher um de outro
  // time move o lead para lá (mesmo comportamento do /chat desktop).
  type GroupStage = { id_time: number; nome_time: string; funil: string; id_etapa: number; nome_funil: string };
  const [groupStages, setGroupStages] = useState<GroupStage[]>([]);
  // Funil escolhido e ainda não aplicado — só a ETAPA grava.
  const [pendingFunnelKey, setPendingFunnelKey] = useState<string | null>(null);
  const [painelAberto, setPainelAberto] = useState(false); // bottom sheet de ações
  const [acaoBusy, setAcaoBusy] = useState(false);
  // ---- Anexo de mídia (clipe): sheet de tipo + input escondido + fila ----
  const [mediaSheetAberto, setMediaSheetAberto] = useState(false);
  const [pendingUploadType, setPendingUploadType] = useState<"image" | "video" | "document" | null>(null);
  const [midiaProgresso, setMidiaProgresso] = useState<{ feitos: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ---- Biblioteca de mídias (T3) — porta chat.tsx:5105-5190, 9805-9890 ----
  const [bibliotecaOpen, setBibliotecaOpen] = useState(false);
  const [bibliotecaAba, setBibliotecaAba] = useState<MidiaKind>("image");
  const [bibliotecaPastas, setBibliotecaPastas] = useState<{ id: number; nome: string }[]>([]);
  const [bibliotecaPasta, setBibliotecaPasta] = useState<{ id: number; nome: string } | null>(null);
  const [bibliotecaItens, setBibliotecaItens] = useState<{ id: number; tipo: string; url: string; nome?: string }[]>([]);
  const [bibliotecaLoading, setBibliotecaLoading] = useState(false);
  // Seleção por toque (decisão do Eduardo, 2026-09-04): um item por vez, NUNCA
  // envia sozinha — só o botão "Enviar" do rodapé dispara.
  const [bibliotecaSelecionada, setBibliotecaSelecionada] = useState<number | null>(null);
  const [bibliotecaEnviando, setBibliotecaEnviando] = useState(false);
  // ---- Respostas rápidas (picker por "/") — porta chat.tsx:5193-5218, 8295-8325 ----
  const [respPickerAberto, setRespPickerAberto] = useState(false);
  const [respostas, setRespostas] = useState<RespostaRapida[]>([]);
  const [respLoading, setRespLoading] = useState(false);
  const respostasCarregadasRef = useRef(false); // equivalente a respostasCarregadas do desktop
  // mídia em tela cheia (toque na imagem/vídeo do balão)
  const [fsMedia, setFsMedia] = useState<{ kind: MidiaKind; src: string } | null>(null);
  // ---- Templates (janela de 24h expirada + conta oficial) ----
  const [tplOpen, setTplOpen] = useState(false);
  const [tplLista, setTplLista] = useState<ChatTemplate[]>([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplBusca, setTplBusca] = useState("");
  const [tplSel, setTplSel] = useState<ChatTemplate | null>(null);
  const [tplVars, setTplVars] = useState<Record<string, string>>({});
  const [tplEnviando, setTplEnviando] = useState(false);
  const [tplErro, setTplErro] = useState("");
  // modal de cards
  const [cardOpen, setCardOpen] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardFunnels, setCardFunnels] = useState<CardFunnel[]>([]);
  const [cardSelFunnel, setCardSelFunnel] = useState<number | null>(null);
  const [cardGroups, setCardGroups] = useState<CardGroup[]>([]);
  const [cardValues, setCardValues] = useState<Record<number, string>>({});

  const fetchRef = useRef<() => void>(() => {});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Antitempestade de refetch (admin master recebe evento realtime de TODA a
  // plataforma): cache dos times do grupo (1 fetch por mount), trava de fetch
  // em voo + fila de 1 pra não perder o último evento.
  const teamIdsRef = useRef<string[] | null>(null);
  const fetchingRef = useRef(false);
  const refetchQueuedRef = useRef(false);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /* Teclado virtual: ajusta o container à área REALMENTE visível (VisualViewport).
     Sem isso, ao focar o input o teclado cobre o composer ("fica cortado") e em
     alguns navegadores o layout não volta ao fechar. Aqui o .cmob encolhe pra
     caber acima do teclado e restaura quando ele fecha.

     ⚠️ REGRA DO AJUSTE (Story 13): SÓ ENCOLHE, NUNCA FIXA O ESTADO NORMAL.
     A versão anterior setava `height: vv.height` sempre, inclusive de teclado
     fechado. Como o iOS não dispara `resize` de forma confiável quando o teclado
     fecha JUNTO com uma navegação (entrar/sair da conversa desmonta o input
     focado) nem ao voltar do background na PWA, a altura pequena ficava
     congelada: o .cmob tem overflow:hidden, então a tela aparecia cortada na
     metade com o fundo cinza embaixo (relatado em produção, 04/08).
     Agora o estado normal é o CSS (height:100dvh) e o inline só existe enquanto
     o teclado está de fato aberto. Evento perdido deixa de virar tela quebrada:
     no pior caso o composer fica coberto por um instante e se resolve sozinho. */
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    /* Folga sobre a barra do navegador/URL, que também encolhe o visual viewport.
       Teclado de iPhone come 250px+; barra de endereço, ~60px. */
    const TECLADO_MIN = 120;
    let rafId = 0;
    let timerId = 0;
    const apply = () => {
      const el = rootRef.current;
      if (!el) return;
      const tecladoAberto = window.innerHeight - vv.height > TECLADO_MIN;
      if (tecladoAberto) {
        el.style.height = `${vv.height}px`;
        el.style.transform = vv.offsetTop ? `translateY(${vv.offsetTop}px)` : "";
      } else {
        // Devolve o controle pro CSS (100dvh) — é isso que se autocorrige.
        el.style.height = "";
        el.style.transform = "";
      }
    };
    /* Gatilhos em que o iOS só reporta o viewport final DEPOIS da animação:
       remede no frame seguinte e de novo ao fim da transição do teclado. */
    const applyLater = () => {
      apply();
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(apply);
      window.clearTimeout(timerId);
      timerId = window.setTimeout(apply, 300);
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    window.addEventListener("orientationchange", applyLater);
    window.addEventListener("pageshow", applyLater);
    document.addEventListener("visibilitychange", applyLater);
    document.addEventListener("focusout", applyLater);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timerId);
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      window.removeEventListener("orientationchange", applyLater);
      window.removeEventListener("pageshow", applyLater);
      document.removeEventListener("visibilitychange", applyLater);
      document.removeEventListener("focusout", applyLater);
      const el = rootRef.current;
      if (el) {
        el.style.height = "";
        el.style.transform = "";
      }
    };
  }, []);

  /* Carrega a lista de conversas espelhando fetchContactsFull() do chat.tsx desktop.
     3 cenários, idênticos ao desktop:
       - admin master no time 159  -> id_cliente=159 + id_user + group_by_number=1
       - admin master (demais)     -> carrega os times do grupo e busca TODOS (id_cliente=ids,join)
       - atendente comum           -> id_cliente + instanceName (resolvido se faltar) + id_user
     Sem isso, o login admin (ou login sem instanceName no localStorage) vinha vazio. */
  const fetchContacts = useCallback(async () => {
    // Fetch em voo: enfileira 1 refetch pro final em vez de empilhar requests
    // (visto em produção: 6 contacts_full pendentes simultâneos).
    if (fetchingRef.current) {
      refetchQueuedRef.current = true;
      return;
    }
    fetchingRef.current = true;
    try {
      setErro(null);
      const admin159 = isAdminMaster && idTime === TIME_159;

      // 1) Admin master (não-159): carrega os times do grupo p/ buscar todos
      //    (cacheado por mount — a lista de times não muda no meio da sessão)
      let teamIds: string[] = [];
      if (isAdminMaster && !admin159 && idGrupo) {
        if (teamIdsRef.current) {
          teamIds = teamIdsRef.current;
        } else {
          try {
            const r = await fetch(`${API_URL}/pegartimecompletochat`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id_cliente: idGrupo }),
            });
            if (r.ok) {
              const lista = await r.json();
              if (Array.isArray(lista)) {
                teamIds = lista.map((t: any) => String(t.id));
                if (teamIds.length) teamIdsRef.current = teamIds;
              }
            }
          } catch {
            /* segue sem admin-all; cai no fallback abaixo */
          }
        }
      }
      const isAllTeams = isAdminMaster && teamIds.length > 0;

      // 2) Atendente sem instanceName no localStorage: resolve via /instanciaatual
      let inst = instanceName;
      if (!inst && !isAllTeams && !admin159 && idTime) {
        try {
          const r = await fetch(`${API_URL}/instanciaatual`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_cliente: idTime }),
          });
          if (r.ok) {
            const d = await r.json();
            inst = d.instanceName || d.instancename || null;
            if (inst) {
              localStorage.setItem("instanceName", inst);
              setInstanceName(inst);
            }
          }
        } catch {
          /* segue sem instanceName */
        }
      }

      // 3) Monta a query igual ao desktop
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (admin159) {
        params.set("id_cliente", TIME_159);
        if (idUser) params.set("id_user", idUser);
        params.set("group_by_number", "1");
      } else if (isAllTeams) {
        params.set("id_cliente", teamIds.join(","));
      } else {
        if (idTime) params.set("id_cliente", idTime);
        if (inst) params.set("instanceName", inst);
        if (idUser) params.set("id_user", idUser);
      }

      const res = await fetch(`${API_URL}/contacts_full?${params.toString()}`);
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      const raw: Contact[] = Array.isArray(data) ? data : data.contacts || data.contatos || [];
      // enriquece lastTimestamp -> number (API pode mandar ISO string ou snake_case), igual desktop
      const list = raw.map((c) => {
        let lastTimestamp = c.lastTimestamp;
        if (typeof lastTimestamp !== "number") {
          if (c.lastTimestamp) lastTimestamp = new Date(c.lastTimestamp as any).getTime();
          else if ((c as any).last_timestamp) lastTimestamp = new Date((c as any).last_timestamp).getTime();
          else if (c.messages?.length) {
            const ts = c.messages.map((m) => new Date(m.timestamp).getTime()).filter((t) => !isNaN(t));
            lastTimestamp = ts.length ? Math.max(...ts) : undefined;
          }
        }
        return { ...c, lastTimestamp, unread_count: c.unread_count ?? 0 };
      });
      list.sort((a, b) => (b.lastTimestamp ?? 0) - (a.lastTimestamp ?? 0));
      setContatos(list);
    } catch (e: any) {
      setErro(e?.message || "Falha ao carregar conversas");
    } finally {
      setLoading(false);
      fetchingRef.current = false;
      // Chegou evento enquanto buscava? Roda 1 refetch pra não ficar defasado.
      if (refetchQueuedRef.current) {
        refetchQueuedRef.current = false;
        fetchRef.current();
      }
    }
  }, [idTime, idUser, idGrupo, isAdminMaster, instanceName]);

  fetchRef.current = fetchContacts;

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  /* ---- Som de mensagem nova (Story 9) ----
     Beep curto via WebAudio (sem asset externo). A política de autoplay dos
     browsers exige um gesto do usuário antes de tocar som: destravamos o
     AudioContext no primeiro toque/clique. Com o app FECHADO quem toca é o
     sistema (som padrão da notificação push). */
  const audioCtxRef = useRef<AudioContext | null>(null);
  const lastBeepRef = useRef(0);
  useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) audioCtxRef.current = new AC();
      }
      audioCtxRef.current?.resume?.().catch(() => {});
    };
    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("click", unlock, { once: true });
    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, []);
  const tocarSomMsg = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state !== "running") return;
    const agora = Date.now();
    if (agora - lastBeepRef.current < 2000) return; // rajada de mensagens = 1 beep só
    lastBeepRef.current = agora;
    const t0 = ctx.currentTime;
    // 2 notas curtas ascendentes (lá5 -> ré6), estilo aviso de mensageiro
    [880, 1174.66].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0, t0 + i * 0.12);
      g.gain.linearRampToValueAtTime(0.22, t0 + i * 0.12 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + i * 0.12 + 0.25);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t0 + i * 0.12);
      o.stop(t0 + i * 0.12 + 0.3);
    });
  }, []);

  /* realtime: novo chat / atualização / novo contato -> recarrega (debounce) */
  useEffect(() => {
    if (!idTime) return;
    let supabase: any;
    try {
      supabase = getSupabaseRealtime();
    } catch {
      return;
    }
    if (!supabase) return;
    const scheduleRefetch = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // 4s (era 800ms): admin master recebe evento de toda a plataforma; com
      // 800ms a lista refazia contacts_full continuamente em horário de pico.
      debounceRef.current = setTimeout(() => fetchRef.current(), 4000);
    };
    // Admin master monitora todos os times -> sem filtro de id_time (RLS do token limita o escopo).
    // Atendente comum -> filtra pelo próprio time.
    const chatsOpts: any = { event: "*", schema: "public", table: "chats" };
    const contatosOpts: any = { event: "INSERT", schema: "public", table: "contatos" };
    if (!isAdminMaster) {
      chatsOpts.filter = `id_time=eq.${idTime}`;
      contatosOpts.filter = `id_time=eq.${idTime}`;
    }
    const ch = supabase
      .channel(`cmob-chat-${isAdminMaster ? "admin" : idTime}`)
      .on("postgres_changes", chatsOpts, (payload: any) => {
        // mensagem RECEBIDA nova -> beep (enviadas/updates não tocam)
        if (payload?.eventType === "INSERT" && payload?.new?.status === "received") tocarSomMsg();
        scheduleRefetch();
      })
      .on("postgres_changes", contatosOpts, scheduleRefetch)
      .subscribe();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      try {
        supabase.removeChannel(ch);
      } catch {
        /* noop */
      }
    };
  }, [idTime, isAdminMaster]);

  /* realtime da ETAPA/FUNIL (funil_fluxo @ banco CRM, projeto diferente do chat):
     quando o lead muda de etapa em qualquer outro lugar (dash, /chat desktop,
     automação, outro aparelho), a lista aqui reflete SEM depender de mensagem
     nova. É o que faz a etapa exibida — e o troféu da etapa-meta — aparecer na
     hora em vez de esperar o próximo INSERT em `chats`.
     Espelha aplicarLeadFunilRealtime do desktop (chat.tsx), inclusive o filtro:
     admin master escuta o grupo todo, atendente comum só o próprio time. */
  const stagesRef = useRef<Stage[]>([]);
  useEffect(() => {
    stagesRef.current = stages;
  }, [stages]);
  const leadDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!(isAdminMaster ? idGrupo : idTime)) return;
    let supabase: any;
    try {
      supabase = getSupabaseRealtimeCRM();
    } catch {
      return;
    }
    if (!supabase) return;
    const filtro = isAdminMaster ? `id_cliente=eq.${idGrupo}` : `id_time=eq.${idTime}`;
    const ch = supabase
      .channel(`cmob-leads-${isAdminMaster ? `g${idGrupo}` : idTime}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "funil_fluxo", filter: filtro },
        (payload: any) => {
          const row = payload?.new;
          if (!row || row.fone == null) return;
          const stage = stagesRef.current.find((s) => Number(s.id) === Number(row.id_funil));
          if (!stage) {
            /* Etapa fora do cache local (admin master vê times cujas etapas não
               foram carregadas): sem nome pra aplicar, recarrega a lista. Mesmo
               debounce de 4s do realtime de chats — admin recebe evento de toda
               a plataforma e o refetch é caro. */
            if (leadDebounceRef.current) clearTimeout(leadDebounceRef.current);
            leadDebounceRef.current = setTimeout(() => fetchRef.current(), 4000);
            return;
          }
          const alvo = soDigitos(row.fone);
          if (!alvo) return;
          setContatos((prev) =>
            prev.map((c) =>
              soDigitos(c.fone) === alvo ? { ...c, nome_funil: stage.nome_funil, funil: stage.funil } : c
            )
          );
        }
      )
      .subscribe();
    return () => {
      if (leadDebounceRef.current) clearTimeout(leadDebounceRef.current);
      try {
        supabase.removeChannel(ch);
      } catch {
        /* noop */
      }
    };
  }, [idTime, idGrupo, isAdminMaster]);

  const selected = useMemo(() => contatos.find((c) => c.id === selectedId) || null, [contatos, selectedId]);

  /* id_cliente correto para ações sobre o contato: admin master usa o id_time do
     contato; atendente comum usa o próprio id_cliente (espelha o desktop). */
  const idClienteDoContato = useCallback(
    (c: Contact | null): string | null => {
      if (isAdminMaster && c?.id_time) return String(c.id_time);
      return idTime;
    },
    [isAdminMaster, idTime]
  );

  /* Carrega etapas (/pegaretapaschat) e funis (/pegarpaginas) uma vez ao logar.
     Necessário p/ o toggle de atendimento humano e os selects de funil/etapa. */
  useEffect(() => {
    if (!idTime) return;
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/pegaretapaschat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_cliente: idTime }),
        });
        if (r.ok && vivo) {
          const d = await r.json();
          if (Array.isArray(d)) setStages(d);
        }
      } catch {
        /* mantém vazio */
      }
      try {
        const r = await fetch(`${API_URL}/pegarpaginas`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_cliente: idTime }),
        });
        if (r.ok && vivo) {
          const d = await r.json();
          if (Array.isArray(d)) {
            setFunnels(d.map((item: any) => ({ nome_funil: item.page ?? item.nome_funil ?? "" })).filter((f: Funnel) => f.nome_funil));
          }
        }
      } catch {
        /* mantém vazio */
      }
      // Admin master: funis/etapas de todo o grupo (param é o id_grupo, não o id_time).
      if (isAdminMaster && idGrupo) {
        try {
          const r = await fetch(`${API_URL}/pegarfunisetapas_grupo`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_grupo: idGrupo }),
          });
          if (r.ok && vivo) {
            const d = await r.json();
            if (Array.isArray(d)) setGroupStages(d);
          }
        } catch {
          /* mantém vazio */
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [idTime, isAdminMaster, idGrupo]);

  /* rola pro fim ao abrir conversa ou chegar mensagem */
  useEffect(() => {
    if (view === "chat" && threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [view, selected?.messages?.length, selectedId]);

  const visiveis = useMemo(() => {
    const term = q.trim().toLowerCase();
    return contatos.filter((c) => {
      if (tab === "nao" && !(c.unread_count && c.unread_count > 0)) return false;
      if (filtroFunil && String(c.funil || "") !== filtroFunil) return false;
      if (filtroEtapa && String(c.nome_funil || "") !== filtroEtapa) return false;
      if (!term) return true;
      // String(...) obrigatório: a API pode mandar fone como number — chamar
      // .toLowerCase() direto derrubava o app inteiro (tela branca) ao buscar.
      return (
        String(c.nome ?? "").toLowerCase().includes(term) ||
        String(c.fone ?? "").toLowerCase().includes(term) ||
        String(c.last_message ?? "").toLowerCase().includes(term)
      );
    });
  }, [contatos, q, tab, filtroFunil, filtroEtapa]);

  const naoLidas = useMemo(() => contatos.filter((c) => c.unread_count && c.unread_count > 0).length, [contatos]);

  // etapas disponíveis no select de Etapa do filtro (do funil selecionado, ou todas)
  const etapasDoFiltro = useMemo(() => {
    const lista = filtroFunil ? stages.filter((s) => String(s.funil) === filtroFunil) : stages;
    const nomes = Array.from(new Set(lista.map((s) => s.nome_funil).filter(Boolean)));
    return nomes;
  }, [stages, filtroFunil]);

  // IA ligada = etapa atual do contato tem closer="Sim" e não é atendimento humano
  // (espelha isBot do ContactRow desktop). Mostra o ícone de robô na lista.
  const iaLigada = useCallback(
    (c: Contact): boolean => {
      const e = stages.find(
        (s) => String(s.funil) === String(c.funil || "") && String(s.nome_funil) === String(c.nome_funil || "")
      );
      return !!(e && e.closer === "Sim" && e.atendimento_humano !== "Sim");
    },
    [stages]
  );

  // todas as etapas (de todos os funis) — para o select de etapa-meta nas configs
  const todasEtapas = useMemo(
    () => Array.from(new Set(stages.map((s) => s.nome_funil).filter(Boolean))),
    [stages]
  );

  // lead que atingiu a meta (etapa-meta configurada) -> troféu
  const ehMeta = useCallback(
    (c: Contact): boolean => !!(etapaMeta && String(c.nome_funil || "") === etapaMeta),
    [etapaMeta]
  );

  // ---- Configurações: salvar (localStorage) + aplicar ----
  const salvarConfig = (key: string, v: string) => {
    if (typeof window === "undefined") return;
    if (v) localStorage.setItem(key, v);
    else localStorage.removeItem(key);
  };
  const onFunilFixo = (v: string) => {
    setFunilFixo(v);
    setFiltroFunil(v); // aplica já no filtro da lista
    setFiltroEtapa("");
    salvarConfig("cmob_funil_fixo", v);
  };
  const onEtapaMeta = (v: string) => {
    setEtapaMeta(v);
    salvarConfig("cmob_etapa_meta", v);
  };
  const onEtiquetasCores = (b: boolean) => {
    setEtiquetasCores(b);
    if (typeof window !== "undefined") localStorage.setItem("cmob_etiquetas_cores", b ? "1" : "0");
  };
  // onPushMeta virou parte de atualizarAssinatura (Story 10) — ver adiante.

  /* Re-vincula a assinatura de push ao login ATUAL (flagrado em produção:
     trocar de conta no aparelho mantinha o push do tenant antigo — o login
     limpa o localStorage, mas a subscription do browser continua registrada
     no serviço com o vínculo velho). Se o aparelho tem subscription, regrava
     no serviço com o token da sessão vigente (subscribe substitui por endpoint). */
  useEffect(() => {
    if (!logado) return;
    (async () => {
      try {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return;
        const supaToken = localStorage.getItem("supabase_token") || "";
        // Preferências locais podem ter sido limpas pelo login; msgs assume
        // ligado (a subscription existir = usuário quis push), meta só se local.
        const metaLocal = localStorage.getItem("cmob_push_meta") === "1";
        const r = await fetch("/push/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${supaToken}` },
          body: JSON.stringify({ subscription: sub.toJSON(), msgs: true, meta: metaLocal }),
        });
        if (r.ok) {
          setPushOn(true); // o login limpou cmob_push_on; reflete o estado real
          localStorage.setItem("cmob_push_on", "1");
        }
      } catch {
        /* silencioso: sem rede/serviço, o toggle manual continua disponível */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logado]);

  /* Sair da conta: desregistra o push DESTE aparelho no serviço (senão a conta
     antiga continuaria notificando até o próximo login re-vincular), limpa a
     sessão e volta pro login. A subscription do browser fica — ao logar de novo,
     o re-vínculo do mount reativa as notificações sozinho. */
  const sair = async () => {
    if (!sairConfirm) {
      setSairConfirm(true);
      if (sairTimerRef.current) clearTimeout(sairTimerRef.current);
      sairTimerRef.current = setTimeout(() => setSairConfirm(false), 3500);
      return;
    }
    if (sairTimerRef.current) clearTimeout(sairTimerRef.current);
    try {
      if ("serviceWorker" in navigator && "PushManager" in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await fetch("/push/unsubscribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ endpoint: sub.endpoint }),
          });
        }
      }
    } catch {
      /* sem rede/serviço não impede o logout */
    }
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      /* noop */
    }
    navigate("/mobile-app/login", { replace: true });
  };

  // ---- Push real (Stories 9/10): assina/desassina Web Push no cmob-push ----
  // O escopo espelha a lista (o serviço decide pelas claims do token). Duas
  // preferências independentes na MESMA assinatura: msgs (mensagem nova) e
  // meta (lead chegou em etapa-meta 🏆 — funil.meta no BD, Story 10).
  const atualizarAssinatura = async (msgs: boolean, meta: boolean) => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushMsg("");
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        // iOS só expõe push pra PWA instalada (16.4+); no Safari comum não existe
        setPushMsg("Este navegador não suporta notificações. No iPhone: instale o app na tela de início e ative por lá.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      // Tudo desligado -> remove a assinatura por inteiro
      if (!msgs && !meta) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          try {
            await fetch("/push/unsubscribe", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ endpoint: sub.endpoint }),
            });
          } catch {
            /* servidor fora não impede desligar local */
          }
          await sub.unsubscribe();
        }
        setPushOn(false);
        setPushMeta(false);
        localStorage.setItem("cmob_push_on", "0");
        localStorage.setItem("cmob_push_meta", "0");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setPushMsg("Permissão de notificação negada. Libere nas configurações do navegador/celular.");
        return;
      }
      const r = await fetch("/push/vapidPublicKey");
      if (!r.ok) throw new Error("serviço de push indisponível");
      const vapid = (await r.text()).trim();
      // base64url -> Uint8Array (formato que o pushManager.subscribe exige)
      const pad = "=".repeat((4 - (vapid.length % 4)) % 4);
      const raw = atob((vapid + pad).replace(/-/g, "+").replace(/_/g, "/"));
      const key = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) key[i] = raw.charCodeAt(i);
      const sub =
        (await reg.pushManager.getSubscription()) ||
        (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
      // Auth: o serviço valida o supabase_token (JWT do /login) e tira o tenant
      // das claims — o vínculo NÃO vem deste body (só as preferências vêm).
      const supaToken = localStorage.getItem("supabase_token") || "";
      const resp = await fetch("/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${supaToken}` },
        body: JSON.stringify({ subscription: sub.toJSON(), msgs, meta }),
      });
      if (resp.status === 401) throw new Error("sessão expirada — saia e entre de novo");
      if (!resp.ok) throw new Error("falha ao registrar no servidor");
      setPushOn(msgs);
      setPushMeta(meta);
      localStorage.setItem("cmob_push_on", msgs ? "1" : "0");
      localStorage.setItem("cmob_push_meta", meta ? "1" : "0");
      setPushMsg("Preferências de notificação salvas. ✅");
    } catch (e: any) {
      setPushMsg(`Não deu pra ativar: ${e?.message || "erro desconhecido"}.`);
    } finally {
      setPushBusy(false);
    }
  };

  const marcarLido = useCallback(
    async (c: Contact) => {
      if (!c.unread_count) return;
      setContatos((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread_count: 0 } : x)));
      try {
        await fetch(`${API_URL}/marcarlidos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone: c.fone, id_time: idTime }),
        });
      } catch {
        /* silencioso: já zeramos local */
      }
    },
    [idTime]
  );

  const abrir = (c: Contact) => {
    setSelectedId(c.id);
    setView("chat");
    setDraft("");
    marcarLido(c);
  };
  const voltar = () => setView("list");

  /* Envio real. Espelha sendMessage() do chat.tsx desktop:
     - optimistic update (mensagem temp na thread)
     - api_mode resolvido do localStorage (oficial se instanciaoficial != "0")
     - admin master usa /enviarmensagem_admin com id_time do contato;
       atendente comum usa /enviarmensagem com id_cliente + instanceName
     - em falha: remove a temp e devolve o texto pro campo */
  const enviarMensagem = useCallback(async () => {
    const text = draft.trim();
    if (!text || !selected || enviando) return;

    const fone = selected.fone || "";
    const isAdminMaster = typeof window !== "undefined" && localStorage.getItem("isAdminMaster") === "true";
    const instanciaOficial = typeof window !== "undefined" ? localStorage.getItem("instanciaoficial") : null;
    const hasOfficial = !!(instanciaOficial && instanciaOficial !== "0");
    const apiMode: "official" | "unofficial" = hasOfficial ? "official" : "unofficial";

    const stamp = Date.now();
    const tempKey = `temp-${stamp}`;
    const tempMsg: Message = {
      id: tempKey,
      clientKey: tempKey,
      fone,
      status: "send",
      mensagem: text,
      timestamp: new Date().toISOString(),
      read: false,
      type: "conversation",
    };

    setContatos((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? { ...c, messages: [...(c.messages || []), tempMsg], last_message: `Você: ${text}`, lastTimestamp: stamp }
          : c
      )
    );
    setDraft("");
    setEnviando(true);

    try {
      let url: string;
      let body: Record<string, unknown>;
      if (isAdminMaster) {
        url = `${API_URL}/enviarmensagem_admin`;
        body = { fone, text, id_time: selected.id_time ?? idTime, id_user: idUser, api_mode: apiMode };
      } else {
        url = `${API_URL}/enviarmensagem`;
        body = { fone, text, id_cliente: idTime, id_user: idUser, instanceName, api_mode: apiMode };
      }
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      /* o realtime de chats dispara o refetch e traz a mensagem definitiva,
         substituindo a temp na lista. */
    } catch {
      setContatos((prev) =>
        prev.map((c) =>
          c.id === selectedId
            ? { ...c, messages: (c.messages || []).filter((m) => m.clientKey !== tempKey) }
            : c
        )
      );
      setDraft(text);
      setAviso("Não foi possível enviar. Tente de novo.");
      setTimeout(() => setAviso(""), 3000);
    } finally {
      setEnviando(false);
    }
  }, [draft, selected, selectedId, enviando, idTime, idUser, instanceName]);


  /* ===== Templates fora da janela de 24h (porta chat.tsx desktop) =====
     Regra idêntica ao desktop: login com número oficial da Meta conectado
     (localStorage.instanciaoficial != "0") — ou um dos times historicamente
     liberados — e conversa fora da janela de 24h => digitação bloqueada e envio
     só por template aprovado. */
  const idTimeEnvio = useMemo(
    () => (isAdminMaster && selected?.id_time ? String(selected.id_time) : idTime),
    [isAdminMaster, selected, idTime]
  );
  const contaOficial = useMemo(() => {
    const inst = typeof window !== "undefined" ? localStorage.getItem("instanciaoficial") : null;
    if (inst && inst !== "0") return true;
    return TEMPLATE_TIME_IDS.includes(String(idTimeEnvio ?? ""));
  }, [idTimeEnvio]);
  /* Só entra em modo template com conversa aberta; recalcula a cada mudança de
     mensagens (uma resposta do lead reabre a janela e libera o campo sozinho). */
  const templateMode = useMemo(
    () => !!selected && contaOficial && foraDaJanela24h(selected),
    [selected, contaOficial]
  );

  /* ===== Respostas rápidas (picker por "/") — porta chat.tsx:5193-5218, 8295-8325 =====
     Abre quando o campo começa com "/" e fecha em qualquer outro conteúdo
     (chat.tsx:8778-8784). Carrega a lista do grupo UMA vez por sessão, disparada
     no primeiro "/" (respostasCarregadasRef), nunca no load da tela — assim as
     suítes e2e existentes seguem passando sem mock novo, porque nada é chamado
     antes de o operador digitar a barra. Nunca aparece em templateMode (o campo
     já vem bloqueado nesse estado — chat.tsx:8811). */
  useEffect(() => {
    if (templateMode) {
      if (respPickerAberto) setRespPickerAberto(false);
      return;
    }
    if (!draft.startsWith("/")) {
      if (respPickerAberto) setRespPickerAberto(false);
      return;
    }
    setRespPickerAberto(true);
    if (!respostasCarregadasRef.current && idGrupo) {
      respostasCarregadasRef.current = true;
      setRespLoading(true);
      fetch(`${API_URL}/respostas_rapidas?id_cliente=${idGrupo}&id_user=${idUser || ""}`)
        .then((r) => r.json())
        .then((json) => setRespostas(Array.isArray(json) ? json : []))
        .catch(() => setRespostas([]))
        .finally(() => setRespLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, templateMode, idGrupo, idUser]);

  /* Filtro por titulo OU texto contendo o termo digitado após a "/", espelhando
     chat.tsx:8295-8298. Fora do picker (draft sem "/") a lista fica vazia. */
  const respostasFiltradas = useMemo(() => {
    if (!draft.startsWith("/")) return [];
    const termo = draft.slice(1).toLowerCase();
    if (!termo) return respostas;
    return respostas.filter(
      (r) => (r.titulo || "").toLowerCase().includes(termo) || (r.texto || "").toLowerCase().includes(termo)
    );
  }, [draft, respostas]);

  /* Escolher SUBSTITUI o campo inteiro pelo texto, igual chat.tsx:5208
     (setNewMessage(r.texto || "")) — sem anexo, sem variável, sem placeholder. */
  const escolherResposta = (r: RespostaRapida) => {
    setDraft(r.texto || "");
    setRespPickerAberto(false);
  };

  const abrirTemplates = async () => {
    if (!selected || !idTimeEnvio) return;
    setTplSel(null);
    setTplVars({});
    setTplBusca("");
    setTplErro("");
    setTplOpen(true);
    setTplLoading(true);
    try {
      const res = await fetch(`${API_URL}/chat/templates_db`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_time: idTimeEnvio }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data: any[] = await res.json();
      setTplLista(
        (Array.isArray(data) ? data : []).map((t: any): ChatTemplate => {
          const body: string = t.body || "";
          const vars: string[] = Array.from(
            new Set((body.match(/\{\{(\d+)\}\}/g) || []).map((v: string) => v.replace(/\D/g, "")))
          ).sort((a, b) => Number(a) - Number(b));
          return {
            name: t.name,
            language: t.language || "pt_BR",
            body,
            headerFormat: t.headerFormat || "",
            headerImage: t.headerImage || "",
            vars,
            botoes: Array.isArray(t.botoes) ? t.botoes : [],
          };
        })
      );
    } catch {
      setTplErro("Não deu pra carregar os templates da conta.");
      setTplLista([]);
    } finally {
      setTplLoading(false);
    }
  };

  /* Seleciona e pré-preenche {{1}} com o primeiro nome do lead (igual desktop,
     inclusive normalizando NOME TODO MAIÚSCULO -> Nome). */
  const selecionarTemplate = (tpl: ChatTemplate) => {
    setTplSel(tpl);
    setTplErro("");
    let nomeLead = (selected?.nome || "").trim().split(/\s+/)[0] || "Cliente";
    if (nomeLead.length > 1 && (nomeLead === nomeLead.toUpperCase() || nomeLead === nomeLead.toLowerCase())) {
      nomeLead = nomeLead.charAt(0).toUpperCase() + nomeLead.slice(1).toLowerCase();
    }
    const init: Record<string, string> = {};
    tpl.vars.forEach((n) => {
      init[n] = n === "1" ? nomeLead : "";
    });
    setTplVars(init);
  };

  const enviarTemplate = async () => {
    if (tplEnviando || !selected || !tplSel || !idTimeEnvio) return;
    const faltando = tplSel.vars.filter((n) => !(tplVars[n] || "").trim());
    if (faltando.length) {
      setTplErro(`Preencha: ${faltando.map((n) => `{{${n}}}`).join(", ")}`);
      return;
    }
    const fone = selected.fone || "";
    const renderedBody = tplSel.body.replace(/\{\{(\d+)\}\}/g, (_m, n) => tplVars[n] ?? `{{${n}}}`);
    // Componentes Meta: header de mídia aprovado + variáveis do body na ordem.
    const components: any[] = [];
    if (tplSel.headerFormat === "IMAGE" && tplSel.headerImage) {
      components.push({ type: "header", parameters: [{ type: "image", image: { link: tplSel.headerImage } }] });
    }
    if (tplSel.vars.length) {
      components.push({ type: "body", parameters: tplSel.vars.map((n) => ({ type: "text", text: tplVars[n] || "" })) });
    }

    const stamp = Date.now();
    const tempKey = `temp-tpl-${stamp}`;
    const tempMsg: Message = {
      id: tempKey,
      clientKey: tempKey,
      fone,
      status: "send",
      mensagem: renderedBody,
      timestamp: new Date(stamp).toISOString(),
      read: false,
      type: "conversation",
      template: true,
    };
    setContatos((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? { ...c, messages: [...(c.messages || []), tempMsg], last_message: `Você: ${renderedBody}`, lastTimestamp: stamp }
          : c
      )
    );
    setTplEnviando(true);
    setTplErro("");
    try {
      const res = await fetch(`${API_URL}/enviar_template`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fone,
          id_time: idTimeEnvio,
          id_user: idUser,
          template_name: tplSel.name,
          language: tplSel.language,
          components,
          rendered_body: renderedBody,
        }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      setTplOpen(false);
      setTplSel(null);
      setTplVars({});
    } catch {
      setContatos((prev) =>
        prev.map((c) =>
          c.id === selectedId ? { ...c, messages: (c.messages || []).filter((m) => m.clientKey !== tempKey) } : c
        )
      );
      setTplErro("Erro ao enviar o template. Tente de novo.");
    } finally {
      setTplEnviando(false);
    }
  };

  /* ===== Gravação e envio de áudio (porta chat.tsx desktop p/ mobile) =====
     Diferença deliberada do desktop: aqui o contexto de sessão vem do `sessao`
     CONGELADO no mount (idTime/idUser/isAdminMaster), não de leituras avulsas do
     localStorage — mesma razão da Story 8 (outra aba trocava o tenant no meio do
     uso). Só `instanciaoficial` e `instanceName` seguem do storage, como no envio
     de texto. O contrato do backend é o mesmo do desktop: multipart em
     /enviarmidiameta_admin | /enviarmidiameta | /enviarmidia. */
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [enviandoMidia, setEnviandoMidia] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingCanceledRef = useRef(false);
  const audioMimeRef = useRef<string>("");

  const formatRecordingTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const startRecordingTimer = () => {
    stopRecordingTimer();
    recordingTimerRef.current = setInterval(() => setRecordingDuration((d) => d + 1), 1000);
  };

  const cleanupRecording = () => {
    stopRecordingTimer();
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    setRecordingDuration(0);
  };

  /* mimetype real do arquivo, com fallback por extensão — espelha guessMime()
     do chat.tsx (linhas 4686-4692) MAIS o ramo .ogg/.opus -> audio/ogg, que o
     desktop NÃO tem: preservado do fallback antigo do enviarAudio() do mobile
     (sem ele, áudio com file.type vazio cairia em application/octet-stream nos
     ramos admin/oficial = regressão do áudio). Local porque chat.tsx é INTOCÁVEL. */
  const guessMime = (file: File): string =>
    file.type ||
    (file.name.endsWith(".pdf")
      ? "application/pdf"
      : file.name.endsWith(".png")
      ? "image/png"
      : file.name.endsWith(".jpg") || file.name.endsWith(".jpeg")
      ? "image/jpeg"
      : file.name.endsWith(".mp4")
      ? "video/mp4"
      : file.name.endsWith(".ogg") || file.name.endsWith(".opus")
      ? "audio/ogg"
      : "application/octet-stream");

  const LAST_MESSAGE_POR_MEDIATYPE: Record<"image" | "video" | "document" | "audio", string> = {
    audio: "Você: 🎙️ Áudio",
    image: "Você: 📷 Foto",
    video: "Você: 🎥 Vídeo",
    document: "Você: 📄 Documento",
  };

  /* Envia qualquer mídia (imagem/vídeo/documento/áudio). Espelha sendMediaFile()
     do chat.tsx (linhas 4733-4830), com os mesmos três caminhos de endpoint:
     admin master -> /enviarmidiameta_admin (id_time do contato)
     instância oficial -> /enviarmidiameta_admin (id_time = id_cliente)
     legado Evolution -> /enviarmidia (ou /enviarmidiameta no time 129)
     Generalização do antigo enviarAudio(file): o comportamento de áudio
     (endpoints, campos, avisos) permanece idêntico ao chamar com "audio". */
  const enviarMidia = async (file: File, mediatype: "image" | "video" | "document" | "audio") => {
    const contato = selected;
    if (!contato || !idTime) {
      setAviso("Sessão sem cliente. Recarregue a página.");
      setTimeout(() => setAviso(""), 3000);
      return;
    }
    const fone = contato.fone || "";
    const instanciaOficial = typeof window !== "undefined" ? localStorage.getItem("instanciaoficial") : null;
    const hasOfficial = !!(instanciaOficial && instanciaOficial !== "0");
    const adminTimeId = isAdminMaster ? String(contato.id_time ?? idTime) : null;

    const stamp = Date.now();
    const tempKey = `temp-${mediatype}-${stamp}`;
    const localUrl = URL.createObjectURL(file);
    const tempMsg: Message = {
      id: tempKey,
      clientKey: tempKey,
      fone,
      status: "send",
      mensagem: localUrl,
      timestamp: new Date().toISOString(),
      read: false,
      type: mediatype === "document" ? "document" : mediatype,
      ...(mediatype === "document" ? { doc_name: file.name } : {}),
    };
    setContatos((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? { ...c, messages: [...(c.messages || []), tempMsg], last_message: LAST_MESSAGE_POR_MEDIATYPE[mediatype], lastTimestamp: stamp }
          : c
      )
    );
    setEnviandoMidia(true);

    try {
      const form = new FormData();
      form.append("file", file, file.name);
      form.append("mediatype", mediatype);
      form.append("number", fone);

      let endpoint = "";
      if (isAdminMaster && adminTimeId) {
        form.append("id_time", adminTimeId);
        if (idUser) form.append("id_user", idUser);
        form.append("mimetype", guessMime(file));
        if (mediatype === "document") form.append("caption", "");
        endpoint = `${API_URL}/enviarmidiameta_admin`;
      } else if (hasOfficial) {
        form.append("id_time", idTime);
        if (idUser) form.append("id_user", idUser);
        form.append("mimetype", guessMime(file));
        if (mediatype === "document") form.append("caption", "");
        endpoint = `${API_URL}/enviarmidiameta_admin`;
      } else {
        form.append("id_cliente", idTime);
        if (idUser) form.append("id_user", idUser);
        if (idTime !== "146") {
          if (!instanceName) throw new Error("Conectando número...");
          form.append("instanceName", instanceName);
        }
        if (mediatype === "document") {
          form.append("caption", "");
          form.append("mimetype", guessMime(file));
        }
        endpoint = idTime === "129" ? `${API_URL}/enviarmidiameta` : `${API_URL}/enviarmidia`;
      }

      /* sem header Content-Type: o browser define o boundary do multipart */
      const res = await fetch(endpoint, { method: "POST", body: form });
      if (!res.ok) throw new Error("HTTP " + res.status);
      /* sucesso: mantém o balão temp (blob toca offline) até o realtime trazer o
         definitivo do banco — mesmo comportamento do desktop. O refetch da lista
         descarta a temp; o blob é liberado depois pra não vazar memória na PWA,
         com folga pro usuário reouvir o que acabou de mandar. */
      setTimeout(() => URL.revokeObjectURL(localUrl), 120000);
    } catch {
      setContatos((prev) =>
        prev.map((c) =>
          c.id === selectedId
            ? { ...c, messages: (c.messages || []).filter((m) => m.clientKey !== tempKey) }
            : c
        )
      );
      URL.revokeObjectURL(localUrl);
      setAviso(
        mediatype === "audio"
          ? "Não foi possível enviar o áudio. Tente de novo."
          : "Não foi possível enviar a mídia. Tente de novo."
      );
      setTimeout(() => setAviso(""), 3000);
    } finally {
      setEnviandoMidia(false);
    }
  };

  /* Fila sequencial de anexo (clipe) — porta enfileirarMidias() do chat.tsx
     (linhas 5032-5083): cria os balões temp em ordem de seleção (cada chamada
     de enviarMidia já cria o seu) e envia um a um em laço await, mantendo
     todos na tela. Aqui o contador é estado local simples (mobile não tem o
     progressoEnvio global do desktop); só aparece na tela quando N > 1. */
  const enviarMidiasEmFila = async (
    files: File[],
    mediatype: "image" | "video" | "document"
  ) => {
    if (!files.length) return;
    setMidiaProgresso({ feitos: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        await enviarMidia(files[i], mediatype);
        setMidiaProgresso({ feitos: i + 1, total: files.length });
      }
    } finally {
      setMidiaProgresso(null);
    }
  };

  /* ===== Biblioteca de mídias (T3) — porta chat.tsx:5105-5190, 9805-9890 =====
     Lista pastas + mídias do grupo por tipo (id_cliente = idGrupo, NUNCA idTime)
     e envia por URL, sem re-upload.
     ⚠️ ÚNICA divergência deliberada do desktop (Decisão do Eduardo, 04/09): lá o
     toque na miniatura ENVIA na hora (chat.tsx:9870, onClick={() =>
     enviarMidiaBiblioteca(m)}); aqui o toque só SELECIONA (marca visual, um
     item por vez) e só o botão "Enviar" do rodapé chama /enviarmidia_url — num
     grid com rolagem por toque um tap acidental mandaria mídia pra um cliente
     real. Trocar de aba ou entrar/sair de pasta LIMPA a seleção. */
  const BIBLIOTECA_ABAS: { key: MidiaKind; label: string }[] = [
    { key: "image", label: "Imagens" },
    { key: "video", label: "Vídeos" },
    { key: "audio", label: "Áudios" },
    { key: "document", label: "Documentos" },
  ];

  const carregarBibliotecaPastas = async (tipo: MidiaKind) => {
    if (!idGrupo) {
      setBibliotecaPastas([]);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/biblioteca/pastas?id_cliente=${idGrupo}&tipo=${tipo}`);
      const json = res.ok ? await res.json() : [];
      setBibliotecaPastas(Array.isArray(json) ? json : []);
    } catch {
      setBibliotecaPastas([]);
    }
  };

  const carregarBibliotecaMidias = async (tipo: MidiaKind, pasta: { id: number; nome: string } | null) => {
    if (!idGrupo) {
      setBibliotecaItens([]);
      return;
    }
    setBibliotecaLoading(true);
    try {
      const idPasta = pasta ? String(pasta.id) : "null";
      const res = await fetch(`${API_URL}/biblioteca?id_cliente=${idGrupo}&tipo=${tipo}&id_pasta=${idPasta}`);
      const json = res.ok ? await res.json() : [];
      setBibliotecaItens(Array.isArray(json) ? json : []);
    } catch {
      setBibliotecaItens([]);
    } finally {
      setBibliotecaLoading(false);
    }
  };

  const abrirBiblioteca = () => {
    if (!selected) return;
    setMediaSheetAberto(false);
    setBibliotecaAba("image");
    setBibliotecaPasta(null);
    setBibliotecaSelecionada(null);
    setBibliotecaOpen(true);
    carregarBibliotecaPastas("image");
    carregarBibliotecaMidias("image", null);
  };

  /* Trocar de aba volta à raiz e recarrega pastas + mídias (chat.tsx:5137-5142). */
  const trocarAbaBiblioteca = (tipo: MidiaKind) => {
    setBibliotecaAba(tipo);
    setBibliotecaPasta(null);
    setBibliotecaSelecionada(null);
    carregarBibliotecaPastas(tipo);
    carregarBibliotecaMidias(tipo, null);
  };

  /* Entrar em pasta recarrega só as mídias (chat.tsx:5144-5147). */
  const entrarPastaBiblioteca = (p: { id: number; nome: string }) => {
    setBibliotecaPasta(p);
    setBibliotecaSelecionada(null);
    carregarBibliotecaMidias(bibliotecaAba, p);
  };

  const voltarRaizBiblioteca = () => {
    if (!bibliotecaPasta) return;
    setBibliotecaPasta(null);
    setBibliotecaSelecionada(null);
    carregarBibliotecaMidias(bibliotecaAba, null);
  };

  /* Toque na miniatura: SELECIONA (marca visual, um item por vez) — NUNCA
     chama /enviarmidia_url. Tocar de novo no mesmo item desmarca. */
  const alternarSelecaoBiblioteca = (id: number) => {
    setBibliotecaSelecionada((prev) => (prev === id ? null : id));
  };

  /* Envia a mídia MARCADA por URL (sem re-upload) — espelha
     enviarMidiaBiblioteca() do chat.tsx (5150-5190). Único disparo possível:
     o botão "Enviar" do rodapé chama esta função. */
  const enviarBibliotecaSelecionada = async () => {
    const contato = selected;
    const m = bibliotecaItens.find((it) => it.id === bibliotecaSelecionada);
    if (!contato || !m || !idTime || bibliotecaEnviando) return;
    const fone = contato.fone || "";
    const adminTimeId = isAdminMaster ? String(contato.id_time ?? idTime) : idTime;
    const tipo = m.tipo === "document" ? "document" : m.tipo;

    const stamp = Date.now();
    const tempKey = `temp-bib-${stamp}`;
    const tempMsg: Message = {
      id: tempKey,
      clientKey: tempKey,
      fone,
      status: "send",
      mensagem: m.url,
      timestamp: new Date().toISOString(),
      read: false,
      type: tipo,
      ...(tipo === "document" ? { doc_name: m.nome || "documento" } : {}),
    };
    setContatos((prev) =>
      prev.map((c) =>
        c.id === selectedId
          ? {
              ...c,
              messages: [...(c.messages || []), tempMsg],
              last_message: LAST_MESSAGE_POR_MEDIATYPE[tipo as "image" | "video" | "document" | "audio"] || "Você: mídia",
              lastTimestamp: stamp,
            }
          : c
      )
    );
    setBibliotecaEnviando(true);
    setBibliotecaOpen(false); // fecha o modal ao disparar o envio, igual chat.tsx:5174

    try {
      const res = await fetch(`${API_URL}/enviarmidia_url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone, id_time: adminTimeId, id_user: idUser, url: m.url, tipo: m.tipo }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
    } catch {
      setContatos((prev) =>
        prev.map((c) =>
          c.id === selectedId
            ? { ...c, messages: (c.messages || []).filter((mm) => mm.clientKey !== tempKey) }
            : c
        )
      );
      setAviso("Não foi possível enviar a mídia da biblioteca.");
      setTimeout(() => setAviso(""), 3000);
    } finally {
      setBibliotecaEnviando(false);
      setBibliotecaSelecionada(null);
    }
  };

  const startRecording = async () => {
    if (isRecording || enviandoMidia || !selected) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setAviso("Seu navegador não suporta gravação de áudio.");
      setTimeout(() => setAviso(""), 3500);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      recordingCanceledRef.current = false;

      /* Preferir OGG/OPUS (nota de voz nativa do WhatsApp); iOS/Safari não suporta
         e cai no fallback WebM/MP4 do próprio aparelho. */
      const candidates = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      /* WebViews antigas têm MediaRecorder sem isTypeSupported: sem esta guarda o
         TypeError caía no catch e virava "permissão negada", diagnóstico errado.
         Sem a função, deixa o próprio aparelho escolher o formato. */
      const mimeType =
        typeof MediaRecorder.isTypeSupported === "function"
          ? candidates.find((m) => MediaRecorder.isTypeSupported(m)) || ""
          : "";
      audioMimeRef.current = mimeType;

      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const wasCanceled = recordingCanceledRef.current;
        const chunks = [...audioChunksRef.current];
        const usedMime = audioMimeRef.current || recorder.mimeType || "audio/webm";
        cleanupRecording();
        if (wasCanceled || chunks.length === 0) return;
        const blob = new Blob(chunks, { type: usedMime });
        const ext = usedMime.includes("ogg") ? "ogg" : usedMime.includes("mp4") ? "m4a" : "webm";
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
        enviarMidia(file, "audio");
      };

      recorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);
      startRecordingTimer();
    } catch {
      setAviso("Permissão de microfone negada ou erro ao iniciar.");
      setTimeout(() => setAviso(""), 3500);
      cleanupRecording();
    }
  };

  const pauseRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording || isPaused) return;
    try {
      r.pause();
      setIsPaused(true);
      stopRecordingTimer();
    } catch {
      /* recorder já parado */
    }
  };

  const resumeRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording || !isPaused) return;
    try {
      r.resume();
      setIsPaused(false);
      startRecordingTimer();
    } catch {
      /* recorder já parado */
    }
  };

  const sendRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording) return;
    recordingCanceledRef.current = false;
    try {
      if (isPaused) r.resume(); // garante a emissão do último chunk
      r.stop(); // dispara onstop -> envia
    } catch {
      cleanupRecording();
    }
  };

  const cancelRecording = () => {
    const r = mediaRecorderRef.current;
    if (!r || !isRecording) return;
    recordingCanceledRef.current = true;
    try {
      if (isPaused) r.resume();
      r.stop(); // dispara onstop -> descarta (canceled = true)
    } catch {
      cleanupRecording();
    }
  };

  /* Solta o microfone ao desmontar: sem isso o indicador de gravação do sistema
     fica aceso depois de sair da tela. Marca como CANCELADO antes de parar —
     sem isso o onstop trata como envio e manda o áudio pela metade pro cliente
     quando a tela desmonta no meio da gravação (logout, navegação, guard). */
  useEffect(() => {
    return () => {
      recordingCanceledRef.current = true;
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      audioStreamRef.current?.getTracks().forEach((t) => t.stop());
      try {
        mediaRecorderRef.current?.stop();
      } catch {
        /* já parado */
      }
    };
  }, []);

  /* Ao sair da conversa (ou trocar de contato) durante uma gravação, descarta. */
  useEffect(() => {
    if (isRecording) cancelRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, view]);

  /* ===== Ações sobre o contato (espelham chat.tsx desktop) ===== */
  const aplicarLocal = (foneAlvo: string, patch: Partial<Contact>) => {
    setContatos((prev) => prev.map((c) => (c.fone === foneAlvo ? { ...c, ...patch } : c)));
  };

  // POST /atualizaretapa — muda só a etapa (mesmo funil)
  const updateFunnelStage = async (fone: string, novaEtapa: string, idCli: string) => {
    const antes = selected?.nome_funil;
    aplicarLocal(fone, { nome_funil: novaEtapa });
    try {
      const res = await fetch(`${API_URL}/atualizaretapa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone, nova_etapa: novaEtapa, idcliente: idCli }),
      });
      if (!res.ok) throw new Error();
    } catch {
      if (antes !== undefined) aplicarLocal(fone, { nome_funil: antes });
      setAviso("Não foi possível atualizar a etapa.");
      setTimeout(() => setAviso(""), 3000);
    }
  };

  // POST /atualizarjuntochat — muda funil + etapa de uma vez
  const updateFunnelCombined = async (fone: string, novaEtapa: string, novoFunil: string, idCli: string) => {
    const antesE = selected?.nome_funil;
    const antesF = selected?.funil;
    aplicarLocal(fone, { nome_funil: novaEtapa, funil: novoFunil });
    try {
      const res = await fetch(`${API_URL}/atualizarjuntochat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone, nova_etapa: novaEtapa, novo_funil: novoFunil, id_cliente: idCli }),
      });
      if (!res.ok) throw new Error();
    } catch {
      aplicarLocal(fone, { nome_funil: antesE, funil: antesF });
      setAviso("Não foi possível mudar o funil.");
      setTimeout(() => setAviso(""), 3000);
    }
  };

  const mudarEtapa = async (novaEtapa: string) => {
    if (!selected || acaoBusy || novaEtapa === selected.nome_funil) return;
    const idCli = idClienteDoContato(selected);
    if (!selected.fone || !idCli) return;
    setAcaoBusy(true);
    await updateFunnelStage(selected.fone, novaEtapa, idCli);
    setAcaoBusy(false);
  };

  const mudarFunil = async (novoFunil: string) => {
    if (!selected || acaoBusy || novoFunil === selected.funil) return;
    const idCli = idClienteDoContato(selected);
    if (!selected.fone || !idCli) return;
    setAcaoBusy(true);
    const firstStage = stages.find((s) => s.funil === novoFunil);
    if (firstStage?.nome_funil) {
      await updateFunnelCombined(selected.fone, firstStage.nome_funil, novoFunil, idCli);
    } else {
      // funil sem etapa conhecida -> /atualizarpaginachat
      const antesF = selected.funil;
      aplicarLocal(selected.fone, { funil: novoFunil });
      try {
        const res = await fetch(`${API_URL}/atualizarpaginachat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone: selected.fone, nova_pagina: novoFunil, id_cliente: idCli }),
        });
        if (!res.ok) throw new Error();
      } catch {
        aplicarLocal(selected.fone, { funil: antesF });
        setAviso("Não foi possível mudar o funil.");
        setTimeout(() => setAviso(""), 3000);
      }
    }
    setAcaoBusy(false);
  };

  // Atendimento humano: etapa do funil com atendimento_humano === "Sim".
  // Ligado = contato está nessa etapa. Desligar volta pra etapa anterior.
  const humanStage = useMemo(
    () => stages.find((s) => s.funil === selected?.funil && s.atendimento_humano === "Sim") || null,
    [stages, selected?.funil]
  );
  const isHuman = !!(humanStage && humanStage.nome_funil === selected?.nome_funil);

  const toggleAtendimentoHumano = async () => {
    if (!selected || acaoBusy || !humanStage) return;
    const idCli = idClienteDoContato(selected);
    if (!selected.fone || !idCli) return;
    setAcaoBusy(true);
    if (!isHuman) {
      await updateFunnelStage(selected.fone, humanStage.nome_funil, idCli);
    } else {
      try {
        const res = await fetch(`${API_URL}/voltar_etapa_anterior`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fone: selected.fone, id_cliente: idCli }),
        });
        const data = await res.json();
        if (data?.found && data.stage_name) {
          if (data.funnel_name && data.funnel_name !== selected.funil) {
            await updateFunnelCombined(selected.fone, data.stage_name, data.funnel_name, idCli);
          } else {
            await updateFunnelStage(selected.fone, data.stage_name, idCli);
          }
        } else {
          setAviso("Não há etapa anterior para voltar.");
          setTimeout(() => setAviso(""), 3000);
        }
      } catch {
        setAviso("Não foi possível desligar o atendimento.");
        setTimeout(() => setAviso(""), 3000);
      }
    }
    setAcaoBusy(false);
  };

  // Cards: funnels -> groups -> fields (paralelo) + values; edição inline salva por campo.
  const carregarEstruturaCards = async (funnelId: number, fone: string, idCli: string) => {
    setCardLoading(true);
    try {
      const [gRes, vRes] = await Promise.all([
        fetch(`${API_URL}/card/groups?id_funil_pai=${funnelId}&id_cliente=${encodeURIComponent(idCli)}`),
        fetch(`${API_URL}/card/values?id_cliente=${encodeURIComponent(idCli)}&fone=${encodeURIComponent(fone)}`),
      ]);
      const groupsRaw = gRes.ok ? await gRes.json() : [];
      const valsRaw = vRes.ok ? await vRes.json() : [];
      const groups: CardGroup[] = Array.isArray(groupsRaw) ? groupsRaw : [];
      const withFields = await Promise.all(
        groups.map(async (g) => {
          try {
            const fRes = await fetch(`${API_URL}/card/fields?id_grupo=${g.id}`);
            const fields = fRes.ok ? await fRes.json() : [];
            return { ...g, fields: Array.isArray(fields) ? fields : [] };
          } catch {
            return { ...g, fields: [] };
          }
        })
      );
      withFields.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
      withFields.forEach((g) => g.fields.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)));
      const valMap: Record<number, string> = {};
      (Array.isArray(valsRaw) ? valsRaw : []).forEach((v: any) => {
        if (v && v.id_campo != null) valMap[v.id_campo] = v.valor ?? "";
      });
      setCardGroups(withFields);
      setCardValues(valMap);
    } catch {
      setCardGroups([]);
      setCardValues({});
    } finally {
      setCardLoading(false);
    }
  };

  const abrirCards = async () => {
    if (!selected) return;
    const idCli = idClienteDoContato(selected);
    if (!selected.fone || !idCli) return;
    setCardOpen(true);
    setPainelAberto(false);
    setCardLoading(true);
    setCardGroups([]);
    setCardValues({});
    try {
      const fRes = await fetch(`${API_URL}/card/funnels?id_cliente=${encodeURIComponent(idCli)}`);
      const funnelsRaw = fRes.ok ? await fRes.json() : [];
      const cfs: CardFunnel[] = Array.isArray(funnelsRaw) ? funnelsRaw : [];
      setCardFunnels(cfs);
      const match = cfs.find((f) => f.nome === selected.funil) || cfs[0];
      const fid = match?.id ?? null;
      setCardSelFunnel(fid);
      if (fid != null) await carregarEstruturaCards(fid, selected.fone, idCli);
      else setCardLoading(false);
    } catch {
      setCardLoading(false);
    }
  };

  const trocarCardFunnel = async (fid: number) => {
    if (!selected) return;
    const idCli = idClienteDoContato(selected);
    if (!selected.fone || !idCli) return;
    setCardSelFunnel(fid);
    await carregarEstruturaCards(fid, selected.fone, idCli);
  };

  const salvarValorCard = (fieldId: number, valor: string) => {
    if (!selected) return;
    const idCli = idClienteDoContato(selected);
    if (!selected.fone || !idCli) return;
    setCardValues((prev) => ({ ...prev, [fieldId]: valor }));
    fetch(`${API_URL}/card/values`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_cliente: idCli, fone: selected.fone, id_campo: fieldId, valor }),
    }).catch(() => {});
  };

  /* ===== Seletor Funil → Etapa =====
     Duas regras (iguais às do /chat desktop):
       1. trocar o FUNIL não grava nada — só recarrega as etapas; quem move o lead
          é a escolha da ETAPA (antes, trocar de funil caía na primeira etapa);
       2. admin master vê os funis de TODOS os times do grupo, e escolher um de
          outro time transfere o lead (id_time, atendente e histórico juntos).
     A chave do funil carrega o time porque "LEADS" existe em vários. */
  const funnelKeyOf = useCallback(
    (idT: any, funil: string) => `${isAdminMaster ? (idT ?? "") : ""}|${funil || ""}`,
    [isAdminMaster]
  );

  const stageOptions = useMemo(() => {
    if (isAdminMaster && groupStages.length > 0) {
      return groupStages.map((s) => ({
        id_etapa: s.id_etapa, funil: s.funil, nome_funil: s.nome_funil,
        id_time: s.id_time as number | null, nome_time: s.nome_time as string | null,
      }));
    }
    return stages.map((s: any) => ({
      id_etapa: s.id, funil: s.funil, nome_funil: s.nome_funil,
      id_time: null as number | null, nome_time: null as string | null,
    }));
  }, [isAdminMaster, groupStages, stages]);

  const funnelOptions = useMemo(() => {
    const vistos = new Set<string>();
    const out: { key: string; funil: string; id_time: number | null; nome_time: string | null }[] = [];
    stageOptions.forEach((o) => {
      const key = funnelKeyOf(o.id_time, o.funil);
      if (vistos.has(key)) return;
      vistos.add(key);
      out.push({ key, funil: o.funil, id_time: o.id_time, nome_time: o.nome_time });
    });
    return out;
  }, [stageOptions, funnelKeyOf]);

  const currentFunnelKey =
    pendingFunnelKey ?? funnelKeyOf((selected as any)?.id_time, selected?.funil || "");

  // etapas do funil selecionado (para o select de etapa)
  const etapasDoFunil = useMemo(
    () => stageOptions.filter((o) => funnelKeyOf(o.id_time, o.funil) === currentFunnelKey),
    [stageOptions, funnelKeyOf, currentFunnelKey]
  );

  // Com funil pendente, a etapa fica em branco — é ela que dispara a gravação.
  const stageSelectValue = pendingFunnelKey !== null
    ? ""
    : String(etapasDoFunil.find((o) => o.nome_funil === (selected?.nome_funil || ""))?.id_etapa ?? "");

  // Trocar de conversa descarta a escolha de funil pendente.
  useEffect(() => {
    setPendingFunnelKey(null);
  }, [selectedId]);

  const moverLeadParaEtapa = async (idEtapa: string) => {
    const destino = stageOptions.find((o) => String(o.id_etapa) === String(idEtapa));
    const idCli = selected ? idClienteDoContato(selected) : null;
    if (!selected?.fone || !destino || !idCli || acaoBusy) return;

    const trocaTime = destino.id_time != null && String(destino.id_time) !== String(idCli);
    if (trocaTime && !window.confirm(
      `Mover este lead para o time "${destino.nome_time}"?\n\n` +
      `O atendimento e o histórico de mensagens passam para esse time.`
    )) return;

    const antesE = selected.nome_funil;
    const antesF = selected.funil;
    setAcaoBusy(true);
    aplicarLocal(selected.fone, { nome_funil: destino.nome_funil, funil: destino.funil });
    setPendingFunnelKey(null);
    try {
      const res = await fetch(`${API_URL}/mover_lead_funil_etapa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fone: selected.fone, id_time: idCli, id_etapa: destino.id_etapa }),
      });
      if (!res.ok) throw new Error();
    } catch {
      aplicarLocal(selected.fone, { nome_funil: antesE, funil: antesF });
      setAviso("Não foi possível mover o lead.");
      setTimeout(() => setAviso(""), 3000);
    }
    setAcaoBusy(false);
  };

  /* render das mensagens com separadores de dia */
  const renderThread = () => {
    const msgs = selected?.messages || [];
    if (!msgs.length) {
      return <div className="cmob-emptythread">Sem mensagens nesta conversa ainda.</div>;
    }
    // lead brasileiro (ou sem idioma definido) não mostra o bloco de tradução
    const mostrarTraducao = !ehPortugues(selected?.idioma);
    let lastDay = "";
    const out: React.ReactNode[] = [];
    msgs.forEach((m: Message, i) => {
      const dl = dayLabel(m.timestamp);
      if (dl && dl !== lastDay) {
        lastDay = dl;
        out.push(
          <div className="cmob-day" key={`d-${i}`}>
            <span>{dl}</span>
          </div>
        );
      }
      if (m.status === "note") {
        out.push(
          <div className="cmob-bub note" key={m.clientKey || m.id || i}>
            <div className="cmob-nh">🗒️ Nota interna</div>
            {m.mensagem}
            <span className="mt">{formatHora(m.timestamp)}</span>
          </div>
        );
        return;
      }
      const isOut = m.status === "send";
      const media = mediaLabel(m.type);
      const read = !!(m.read || m.status_template_wp === "read");
      const kind = midiaRenderizavel(m);
      out.push(
        <div className={`cmob-bub ${isOut ? "out" : "in"}${kind && kind !== "audio" ? " midia" : ""}`} key={m.clientKey || m.id || i}>
          {isAudioPlayable(m) ? (
            /* áudio com URL (storage público, http ou blob otimista) toca no balão;
               sem URL cai na tag, que é o comportamento antigo. */
            <audio className="cmob-audio" controls preload="none" src={m.mensagem} />
          ) : kind === "image" ? (
            /* toque abre em tela cheia (pinch-zoom do sistema funciona lá) */
            <img
              className="cmob-img"
              src={srcMidia(m, "image")}
              alt="Imagem"
              loading="lazy"
              onClick={() => setFsMedia({ kind: "image", src: srcMidia(m, "image") })}
            />
          ) : kind === "video" ? (
            <video className="cmob-video" src={srcMidia(m, "video")} controls preload="metadata" playsInline />
          ) : kind === "audio" ? (
            <audio className="cmob-audio" controls preload="none" src={srcMidia(m, "audio")} />
          ) : kind === "document" ? (
            (() => {
              const href = srcMidia(m, "document");
              const nome = nomeArquivo(m);
              const ext = (nome.split(".").pop() || "").toLowerCase().replace(/[^a-z0-9]/g, "");
              return (
                <a className="cmob-doc" href={href} download={nome} target="_blank" rel="noreferrer">
                  <FileIcon ext={ext} />
                  <span className="cmob-doc-txt">
                    <span className="cmob-doc-nm">{nome}</span>
                    <span className="cmob-doc-sb">{ext ? ext.toUpperCase() : "ARQUIVO"}</span>
                  </span>
                  <span className="cmob-doc-dl">{Icon.download}</span>
                </a>
              );
            })()
          ) : media ? (
            <span className="cmob-mediatag">{media}</span>
          ) : (
            m.mensagem
          )}
          {/* Tradução só em lead estrangeiro: em lead PT-BR o bloco repetia a
              própria mensagem e poluía a conversa. */}
          {m.traduzido && mostrarTraducao && (
            <div className="cmob-trad">
              <b>Tradução</b>
              {m.traduzido}
            </div>
          )}
          {m.audio_transc && (
            <div className="cmob-trad">
              <b>Transcrição</b>
              {m.audio_transc}
            </div>
          )}
          <span className="mt">
            {formatHora(m.timestamp)}
            {isOut && <span className={`cmob-tk ${read ? "read" : ""}`}>{Icon.tickRead}</span>}
          </span>
        </div>
      );
    });
    return out;
  };

  // Sem login: não renderiza o chat (o useEffect acima já redireciona pro /entrar)
  if (!logado) return null;

  return (
    <div className="cmob" ref={rootRef}>
      {/* ---------- LISTA ---------- */}
      <section className={`cmob-view list${view === "chat" ? " pushed" : ""}`}>
        <header className="cmob-head">
          <div className="cmob-titlebar">
            <div className="cmob-title">Conversas</div>
            <span className="cmob-gear" onClick={() => setConfigOpen(true)} aria-label="Configurações">
              {Icon.gear}
            </span>
          </div>
          <div className="cmob-search">
            {Icon.search}
            <input
              value={q}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
              placeholder="Buscar nome, telefone ou mensagem"
            />
          </div>
          <div className="cmob-tabs">
            <span className={`cmob-tab ${tab === "todos" ? "on" : "off"}`} onClick={() => setTab("todos")}>
              Todas
            </span>
            <span className={`cmob-tab ${tab === "nao" ? "on" : "off"}`} onClick={() => setTab("nao")}>
              Não lidas <b className="n">{naoLidas}</b>
            </span>
          </div>
          {(funnels.length > 0 || stages.length > 0) && (
            <div className="cmob-filtros">
              <select
                className={`cmob-filtro${filtroFunil ? " ativo" : ""}`}
                value={filtroFunil}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setFiltroFunil(e.target.value);
                  setFiltroEtapa("");
                }}
              >
                <option value="">Todos os funis</option>
                {funnels.map((f) => (
                  <option key={f.nome_funil} value={f.nome_funil}>
                    {f.nome_funil}
                  </option>
                ))}
              </select>
              <select
                className={`cmob-filtro${filtroEtapa ? " ativo" : ""}`}
                value={filtroEtapa}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFiltroEtapa(e.target.value)}
              >
                <option value="">Todas as etapas</option>
                {etapasDoFiltro.map((nome) => (
                  <option key={nome} value={nome}>
                    {nome}
                  </option>
                ))}
              </select>
              {(filtroFunil || filtroEtapa) && (
                <button
                  className="cmob-filtro-clear"
                  onClick={() => {
                    setFiltroFunil("");
                    setFiltroEtapa("");
                  }}
                  aria-label="Limpar filtros"
                >
                  {Icon.close}
                </button>
              )}
            </div>
          )}
        </header>

        {loading ? (
          <div className="cmob-state">
            <div className="cmob-spin" />
            <small>Carregando conversas...</small>
          </div>
        ) : erro ? (
          <div className="cmob-state">
            <div className="ic">⚠️</div>
            <div>Não foi possível carregar</div>
            <small>{erro}</small>
            <button className="cmob-retry" onClick={() => { setLoading(true); fetchContacts(); }}>
              Tentar de novo
            </button>
          </div>
        ) : !visiveis.length ? (
          <div className="cmob-state">
            <div className="ic">💬</div>
            <div>Nenhuma conversa</div>
          </div>
        ) : (
          <div className="cmob-list">
            {visiveis.map((c) => {
              const unread = !!(c.unread_count && c.unread_count > 0);
              const isVoce = (c.last_message || "").startsWith("Você:");
              return (
                <div className="cmob-conv" key={c.id} onClick={() => abrir(c)}>
                  <div
                    className="cmob-av"
                    style={
                      c.profilePictureUrl
                        ? { backgroundImage: `url(${c.profilePictureUrl})` }
                        : { background: avatarColor(c.nome || String(c.id)) }
                    }
                  >
                    {!c.profilePictureUrl && (initials(c.nome) || Icon.person)}
                  </div>
                  <div className="cmob-mid">
                    <div className="cmob-nm">
                      {ehMeta(c) && (
                        <span className="cmob-trophy" title="Meta atingida">
                          {Icon.trophy}
                        </span>
                      )}
                      {iaLigada(c) && (
                        <span className="cmob-robot" title="IA ativa">
                          {Icon.robot}
                        </span>
                      )}
                      <span className="cmob-nm-txt">{c.nome || c.fone}</span>
                    </div>
                    <div className="cmob-pv">
                      {isVoce && <span className="cmob-tk read">{Icon.tickRead}</span>}
                      {c.last_message || ""}
                    </div>
                  </div>
                  <div className="cmob-rt">
                    <span className={`cmob-tm${unread ? " un" : ""}`}>{formatHora(c.lastTimestamp)}</span>
                    {unread ? (
                      <span className="cmob-badge">{c.unread_count}</span>
                    ) : c.nome_funil ? (
                      <span
                        className="cmob-stage"
                        style={
                          etiquetasCores
                            ? { background: corEtapa(c.nome_funil).bg, color: corEtapa(c.nome_funil).fg }
                            : undefined
                        }
                      >
                        {c.nome_funil}
                      </span>
                    ) : (
                      <span style={{ height: 21 }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------- CONVERSA ---------- */}
      <section className={`cmob-view chat${view === "chat" ? " on" : ""}`}>
        <header className="cmob-chead">
          <span className="cmob-back" onClick={voltar}>
            {Icon.back}
          </span>
          <span className="cmob-cav" style={{ background: selected ? avatarColor(selected.nome || "") : "#0b6b58" }}>
            {selected ? initials(selected.nome) || "" : ""}
          </span>
          <div className="cmob-cid">
            <div className="cmob-cnm">
              {selected && ehMeta(selected) && <span className="cmob-ctrophy">{Icon.trophy}</span>}
              {selected?.nome || formatFone(selected?.fone) || "Conversa"}
            </div>
            {/* fone do lead ao lado do nome (linha de status, fonte menor p/ não
                mexer no visual); só repete se o nome já não for o próprio número */}
            <div className="cmob-cst">
              {selected?.nome && selected?.fone ? <span className="cmob-cfone">{formatFone(selected.fone)}</span> : null}
              {selected?.nome && selected?.fone && selected?.nome_funil ? <span className="cmob-cdot">·</span> : null}
              {selected?.nome_funil || (selected?.nome && selected?.fone ? "" : "online")}
            </div>
          </div>
          {humanStage && (
            <button
              className={`cmob-hsw${isHuman ? " on" : ""}`}
              disabled={acaoBusy}
              onClick={toggleAtendimentoHumano}
              aria-label="Atendimento humano"
              title={isHuman ? "Atendimento humano ligado (tocar p/ desligar)" : "IA ativa (tocar p/ assumir)"}
            >
              <span className="cmob-hsw-ic">{isHuman ? Icon.person : Icon.robot}</span>
              <span className="cmob-hsw-track">
                <span className="cmob-hsw-dot" />
              </span>
            </button>
          )}
          <span className="cmob-cmenu" onClick={() => setPainelAberto(true)} aria-label="Ações">
            {Icon.dots}
          </span>
        </header>

        <div className="cmob-thread" ref={threadRef}>
          {renderThread()}
        </div>

        {aviso && <div className="cmob-footnote">{aviso}</div>}
        {midiaProgresso && midiaProgresso.total > 1 && (
          <div className="cmob-footnote">
            Enviando arquivo {Math.min(midiaProgresso.feitos + 1, midiaProgresso.total)} de {midiaProgresso.total}
          </div>
        )}
        <div className="cmob-foot">
          {/* picker de respostas rápidas: absoluto, ancorado acima do campo.
              O container .cmob (rootRef) já encolhe pro visualViewport visível
              quando o teclado abre (efeito acima), então "acima do campo" aqui
              já nasce acima do teclado de graça — sem cálculo de altura novo. */}
          {!isRecording && !templateMode && respPickerAberto && (
            <div className="cmob-resp-picker">
              <div className="cmob-resp-picker-head">
                Respostas rápidas ({respostasFiltradas.length})
              </div>
              <div className="cmob-resp-picker-list">
                {respLoading ? (
                  <div className="cmob-resp-picker-empty">Carregando...</div>
                ) : respostasFiltradas.length === 0 ? (
                  <div className="cmob-resp-picker-empty">
                    Nada encontrado. Cadastre em "Respostas Rápidas".
                  </div>
                ) : (
                  respostasFiltradas.map((r) => (
                    <div
                      key={r.id}
                      className="cmob-resp-picker-item"
                      /* mousedown com preventDefault: evita que o blur do input
                         dispare antes do click registrar o toque (mesmo padrão
                         do onMouseDown do desktop, chat.tsx). Sem isso o fechamento
                         do picker (guiado pelo próprio draft) corre risco de vir
                         antes do clique em navegadores que reordenam blur/click. */
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => escolherResposta(r)}
                    >
                      <div className="cmob-resp-picker-titulo">{r.titulo}</div>
                      <div className="cmob-resp-picker-texto">{r.texto}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          {isRecording ? (
            /* gravando: a barra inteira vira o painel de gravação */
            <>
              <button className="cmob-crec-del" onClick={cancelRecording} aria-label="Cancelar gravação">
                {Icon.trash}
              </button>
              <div className="cmob-crec">
                <span className={`cmob-recdot${isPaused ? " paused" : ""}`} />
                <span className="cmob-rectime">{formatRecordingTime(recordingDuration)}</span>
                <span className="cmob-rechint">{isPaused ? "Pausado" : "Gravando..."}</span>
                <button
                  className="cmob-crec-pp"
                  onClick={isPaused ? resumeRecording : pauseRecording}
                  aria-label={isPaused ? "Retomar" : "Pausar"}
                >
                  {isPaused ? Icon.play : Icon.pause}
                </button>
              </div>
              <button className="cmob-csend" onClick={sendRecording} aria-label="Enviar áudio">
                {Icon.send}
              </button>
            </>
          ) : templateMode ? (
            /* Fora da janela de 24h em conta oficial: a Meta só aceita template.
               Campo bloqueado e botão único que abre a seleção (igual desktop). */
            <>
              <div className="cmob-cinput bloq">
                <input value="" readOnly placeholder="Janela de 24h expirada. Envie um template" />
              </div>
              <button className="cmob-csend" onClick={abrirTemplates} aria-label="Escolher template">
                {Icon.template}
              </button>
            </>
          ) : (
            <>
              {/* clipe: some em templateMode/isRecording por construção — este
                  é o único branch do composer em que ele é renderizado, igual
                  ao desktop (chat.tsx:8374, 8380). */}
              <button
                type="button"
                className="cmob-clip"
                onClick={() => setMediaSheetAberto(true)}
                disabled={!selected || enviandoMidia}
                aria-label="Anexar arquivo"
                title="Anexar arquivo"
              >
                {Icon.clip}
              </button>
              <div className="cmob-cinput">
                <input
                  value={draft}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key !== "Enter") return;
                    // picker aberto: Enter escolhe a 1ª match em vez de enviar
                    // (espelha chat.tsx:8788-8797). Picker fechado: envia normal.
                    if (respPickerAberto && respostasFiltradas.length > 0) {
                      e.preventDefault();
                      escolherResposta(respostasFiltradas[0]);
                      return;
                    }
                    if (!respPickerAberto) enviarMensagem();
                  }}
                  placeholder="Mensagem"
                />
              </div>
              {draft.trim() ? (
                <button
                  className="cmob-csend"
                  onClick={enviarMensagem}
                  disabled={enviando}
                  aria-label="Enviar"
                >
                  {Icon.send}
                </button>
              ) : (
                /* campo vazio: o botão vira microfone (padrão WhatsApp) */
                <button
                  className="cmob-csend"
                  onClick={startRecording}
                  disabled={enviandoMidia}
                  aria-label="Gravar áudio"
                >
                  {Icon.mic}
                </button>
              )}
            </>
          )}
          {/* input invisível para escolher o(s) arquivo(s) — sempre montado,
              fora do ternário acima, igual ao desktop (chat.tsx:8659-8678) */}
          <input
            type="file"
            ref={fileInputRef}
            multiple
            style={{ display: "none" }}
            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
              const inputEl = e.currentTarget;
              const files = Array.from(inputEl.files || []);
              if (files.length === 0 || !pendingUploadType) {
                inputEl.value = "";
                setPendingUploadType(null);
                return;
              }
              try {
                await enviarMidiasEmFila(files, pendingUploadType);
              } finally {
                inputEl.value = "";
                setPendingUploadType(null);
              }
            }}
          />
        </div>
      </section>

      {/* ---------- BOTTOM SHEET: TIPO DE MÍDIA (clipe) ----------
          Porta o menu do clipe do desktop (chat.tsx:8386-8451), sem o item
          Biblioteca (entra na T3) e sem o accept de câmera dedicado (decisão
          de escopo: image/* já oferece "Tirar Foto" no seletor do iOS). */}
      {mediaSheetAberto && (
        <div className="cmob-sheet-wrap" onClick={() => setMediaSheetAberto(false)}>
          <div className="cmob-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-sheet-grab" />
            <div className="cmob-sheet-title">
              <span>Enviar</span>
              <span className="cmob-sheet-x" onClick={() => setMediaSheetAberto(false)}>
                {Icon.close}
              </span>
            </div>
            <button
              type="button"
              className="cmob-act-btn"
              onClick={() => {
                setPendingUploadType("image");
                setMediaSheetAberto(false);
                const el = fileInputRef.current;
                if (el) {
                  el.accept = "image/*";
                  el.click();
                }
              }}
            >
              📷 Imagem
            </button>
            <button
              type="button"
              className="cmob-act-btn"
              onClick={() => {
                setPendingUploadType("video");
                setMediaSheetAberto(false);
                const el = fileInputRef.current;
                if (el) {
                  el.accept = "video/*";
                  el.click();
                }
              }}
            >
              🎬 Vídeo
            </button>
            <button
              type="button"
              className="cmob-act-btn"
              onClick={() => {
                setPendingUploadType("document");
                setMediaSheetAberto(false);
                const el = fileInputRef.current;
                if (el) {
                  el.accept = "*/*";
                  el.click();
                }
              }}
            >
              📎 Documento
            </button>
            <button
              type="button"
              className="cmob-act-btn"
              onClick={abrirBiblioteca}
            >
              📚 Biblioteca
            </button>
          </div>
        </div>
      )}

      {/* ---------- BOTTOM SHEET: AÇÕES ---------- */}
      {painelAberto && (
        <div className="cmob-sheet-wrap" onClick={() => setPainelAberto(false)}>
          <div className="cmob-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cmob-sheet-grab" />
            <div className="cmob-sheet-title">
              <span>{selected?.nome || selected?.fone || "Conversa"}</span>
              <span className="cmob-sheet-x" onClick={() => setPainelAberto(false)}>
                {Icon.close}
              </span>
            </div>

            {/* Atendimento humano */}
            {humanStage && (
              <div className="cmob-act-row">
                <div className="cmob-act-info">
                  <div className="cmob-act-lb">Atendimento humano</div>
                  <div className="cmob-act-sb">{isHuman ? "Ligado (IA pausada)" : "Desligado (IA ativa)"}</div>
                </div>
                <button
                  className={`cmob-switch${isHuman ? " on" : ""}`}
                  disabled={acaoBusy}
                  onClick={toggleAtendimentoHumano}
                  aria-label="Alternar atendimento humano"
                >
                  <span className="cmob-switch-dot" />
                </button>
              </div>
            )}

            {/* Funil — trocar aqui NÃO grava; só recarrega as etapas */}
            <div className="cmob-act-field">
              <label>{Icon.funnel} Funil</label>
              <select
                value={currentFunnelKey}
                disabled={acaoBusy || !funnelOptions.length}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPendingFunnelKey(e.target.value)}
              >
                {!funnelOptions.some((f) => f.key === currentFunnelKey) && selected?.funil && (
                  <option value={currentFunnelKey}>{selected.funil}</option>
                )}
                {isAdminMaster && groupStages.length > 0
                  ? Array.from(new Set(funnelOptions.map((f) => f.nome_time || "—"))).map((nomeTime) => (
                      <optgroup key={nomeTime} label={nomeTime}>
                        {funnelOptions
                          .filter((f) => (f.nome_time || "—") === nomeTime)
                          .map((f) => (
                            <option key={f.key} value={f.key}>{f.funil}</option>
                          ))}
                      </optgroup>
                    ))
                  : funnelOptions.map((f) => (
                      <option key={f.key} value={f.key}>{f.funil}</option>
                    ))}
              </select>
            </div>

            {/* Etapa — é a escolha que move o lead */}
            <div className="cmob-act-field">
              <label>Etapa</label>
              <select
                value={stageSelectValue}
                disabled={acaoBusy || !etapasDoFunil.length}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => moverLeadParaEtapa(e.target.value)}
              >
                <option value="" disabled>
                  {pendingFunnelKey !== null ? "Escolha a etapa…" : "Etapa"}
                </option>
                {etapasDoFunil.map((o) => (
                  <option key={o.id_etapa} value={o.id_etapa}>
                    {o.nome_funil}
                  </option>
                ))}
              </select>
            </div>

            {/* Ver cards */}
            <button className="cmob-act-btn" onClick={abrirCards}>
              {Icon.card} Ver cards
            </button>
          </div>
        </div>
      )}

      {/* ---------- MODAL: CARDS ---------- */}
      {cardOpen && (
        <div className="cmob-card-modal">
          <header className="cmob-card-head">
            <span className="cmob-card-x" onClick={() => setCardOpen(false)}>
              {Icon.close}
            </span>
            <div className="cmob-card-title">Cards · {selected?.nome || selected?.fone}</div>
          </header>

          {cardFunnels.length > 1 && (
            <div className="cmob-card-funnelsel">
              <select
                value={cardSelFunnel ?? ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => trocarCardFunnel(Number(e.target.value))}
              >
                {cardFunnels.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="cmob-card-body">
            {cardLoading ? (
              <div className="cmob-state">
                <div className="cmob-spin" />
                <small>Carregando cards...</small>
              </div>
            ) : !cardGroups.length ? (
              <div className="cmob-state">
                <div className="ic">🗂️</div>
                <div>Nenhum card neste funil</div>
              </div>
            ) : (
              cardGroups.map((g) => (
                <div className="cmob-card-group" key={g.id}>
                  <div className="cmob-card-gname">{g.nome}</div>
                  {g.fields.map((f) => (
                    <div className="cmob-card-field" key={f.id}>
                      <label>
                        {f.label}
                        {f.obrigatorio ? <b className="req"> *</b> : null}
                      </label>
                      {f.tipo === "select" && f.opcoes ? (
                        <select
                          value={cardValues[f.id] ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => salvarValorCard(f.id, e.target.value)}
                        >
                          <option value="">—</option>
                          {f.opcoes.split(",").map((op) => (
                            <option key={op} value={op.trim()}>
                              {op.trim()}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={cardValues[f.id] ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setCardValues((prev) => ({ ...prev, [f.id]: e.target.value }))
                          }
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) => salvarValorCard(f.id, e.target.value)}
                          placeholder="—"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ---------- MODAL: TEMPLATES (janela de 24h expirada) ---------- */}
      {tplOpen && (
        <div className="cmob-card-modal">
          <header className="cmob-card-head">
            <span className="cmob-card-x" onClick={() => setTplOpen(false)}>
              {Icon.close}
            </span>
            <div className="cmob-card-title">
              {tplSel ? tplSel.name : `Templates · ${selected?.nome || formatFone(selected?.fone)}`}
            </div>
          </header>

          <div className="cmob-card-body">
            <div className="cmob-tpl-aviso">
              Faz mais de 24h que o lead não responde. A Meta só entrega template aprovado.
            </div>
            {tplErro && <div className="cmob-tpl-erro">{tplErro}</div>}

            {tplLoading ? (
              <div className="cmob-state">
                <div className="cmob-spin" />
                <small>Carregando templates...</small>
              </div>
            ) : tplSel ? (
              /* ----- passo 2: preencher variáveis e enviar ----- */
              <>
                <div className="cmob-tpl-prev">
                  {tplSel.headerFormat === "IMAGE" && tplSel.headerImage && (
                    <img className="cmob-tpl-previmg" src={tplSel.headerImage} alt="" loading="lazy" />
                  )}
                  <div className="cmob-tpl-prevtxt">
                    {tplSel.body.replace(/\{\{(\d+)\}\}/g, (_m, n) => tplVars[n] || `{{${n}}}`)}
                  </div>
                  {!!tplSel.botoes?.length && (
                    <div className="cmob-tpl-btns">
                      {tplSel.botoes.map((b, i) => (
                        <span className="cmob-tpl-btn" key={i}>
                          {b.text || "Botão"}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {tplSel.vars.map((n) => (
                  <div className="cmob-card-field" key={n}>
                    <label>{`Variável {{${n}}}`}</label>
                    <input
                      type="text"
                      value={tplVars[n] ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTplVars((prev) => ({ ...prev, [n]: e.target.value }))
                      }
                      placeholder="—"
                    />
                  </div>
                ))}

                <div className="cmob-tpl-acoes">
                  <button className="cmob-tpl-voltar" onClick={() => setTplSel(null)} disabled={tplEnviando}>
                    Trocar template
                  </button>
                  <button className="cmob-tpl-enviar" onClick={enviarTemplate} disabled={tplEnviando}>
                    {tplEnviando ? "Enviando..." : "Enviar template"}
                  </button>
                </div>
              </>
            ) : (
              /* ----- passo 1: escolher da lista ----- */
              <>
                <div className="cmob-tpl-busca">
                  {Icon.search}
                  <input
                    value={tplBusca}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTplBusca(e.target.value)}
                    placeholder="Buscar template"
                  />
                </div>
                {!tplLista.length ? (
                  <div className="cmob-state">
                    <div className="ic">📋</div>
                    <div>Nenhum template disponível nesta conta</div>
                  </div>
                ) : (
                  tplLista
                    .filter((t) => {
                      const termo = tplBusca.trim().toLowerCase();
                      if (!termo) return true;
                      return t.name.toLowerCase().includes(termo) || t.body.toLowerCase().includes(termo);
                    })
                    .map((t) => (
                      <button className="cmob-tpl-item" key={t.name} onClick={() => selecionarTemplate(t)}>
                        <span className="cmob-tpl-nm">{t.name}</span>
                        <span className="cmob-tpl-bd">{t.body}</span>
                      </button>
                    ))
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- MODAL: BIBLIOTECA DE MÍDIAS (T3) ----------
          Porta o modal do desktop (chat.tsx:9805-9890): pastas + mídias do
          grupo por tipo, envio por URL sem re-upload. ⚠️ Toque na miniatura
          SELECIONA (nunca envia) — só o botão "Enviar" do rodapé dispara
          /enviarmidia_url (Decisão do Eduardo, 04/09). */}
      {bibliotecaOpen && (
        <div className="cmob-card-modal">
          <header className="cmob-card-head">
            <span className="cmob-card-x" onClick={() => setBibliotecaOpen(false)}>
              {Icon.close}
            </span>
            <div className="cmob-card-title">Biblioteca de mídias</div>
          </header>

          <div className="cmob-bib-tabs">
            {BIBLIOTECA_ABAS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`cmob-bib-tab${bibliotecaAba === t.key ? " active" : ""}`}
                onClick={() => trocarAbaBiblioteca(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="cmob-card-body">
            {/* Breadcrumb de pasta */}
            <div className="cmob-bib-crumb">
              <button type="button" className="cmob-bib-crumb-link" onClick={voltarRaizBiblioteca}>
                Raiz
              </button>
              {bibliotecaPasta && (
                <>
                  <span className="cmob-bib-crumb-sep">›</span>
                  <span className="cmob-bib-crumb-atual">📁 {bibliotecaPasta.nome}</span>
                </>
              )}
            </div>

            {/* Pastas (só na raiz) */}
            {!bibliotecaPasta && bibliotecaPastas.length > 0 && (
              <div className="cmob-bib-grid">
                {bibliotecaPastas.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className="cmob-bib-pasta"
                    onClick={() => entrarPastaBiblioteca(p)}
                  >
                    <span className="cmob-bib-pasta-ic">📁</span>
                    <span className="cmob-bib-pasta-nm">{p.nome}</span>
                  </button>
                ))}
              </div>
            )}

            {bibliotecaLoading ? (
              <div className="cmob-state">
                <div className="cmob-spin" />
                <small>Carregando...</small>
              </div>
            ) : bibliotecaItens.length === 0 ? (
              !bibliotecaPasta && bibliotecaPastas.length > 0 ? null : (
                <div className="cmob-state">
                  <div className="ic">🗂️</div>
                  <div>Nenhuma mídia aqui</div>
                </div>
              )
            ) : (
              <div className="cmob-bib-grid">
                {bibliotecaItens.map((m) => {
                  const marcado = bibliotecaSelecionada === m.id;
                  return (
                    /* ⚠️ SELECIONA, nunca envia — a única chamada a
                       /enviarmidia_url fica em enviarBibliotecaSelecionada,
                       disparada pelo botão "Enviar" do rodapé. */
                    <button
                      type="button"
                      key={m.id}
                      className={`cmob-bib-item${marcado ? " sel" : ""}`}
                      onClick={() => alternarSelecaoBiblioteca(m.id)}
                      title={m.nome || ""}
                    >
                      {marcado && <span className="cmob-bib-check">✓</span>}
                      {m.tipo === "image" ? (
                        <img className="cmob-bib-thumb" src={m.url} alt={m.nome || ""} loading="lazy" />
                      ) : m.tipo === "video" ? (
                        <video className="cmob-bib-thumb" src={m.url} preload="metadata" muted />
                      ) : (
                        <div className="cmob-bib-thumb cmob-bib-thumb-ic">
                          {m.tipo === "audio" ? "🎵" : "📄"}
                        </div>
                      )}
                      <span className="cmob-bib-nm">{m.nome || "—"}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="cmob-bib-footer">
            <button
              type="button"
              className="cmob-bib-enviar"
              disabled={!bibliotecaSelecionada || bibliotecaEnviando}
              onClick={enviarBibliotecaSelecionada}
            >
              {bibliotecaEnviando ? "Enviando..." : "Enviar"}
            </button>
          </div>
        </div>
      )}

      {/* ---------- VISUALIZADOR DE MÍDIA EM TELA CHEIA ---------- */}
      {fsMedia && (
        <div className="cmob-fs" onClick={() => setFsMedia(null)}>
          <span className="cmob-fs-x" aria-label="Fechar">
            {Icon.close}
          </span>
          {fsMedia.kind === "video" ? (
            <video src={fsMedia.src} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={fsMedia.src} alt="" onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}

      {/* ---------- MODAL: CONFIGURAÇÕES ---------- */}
      {configOpen && (
        <div className="cmob-card-modal">
          <header className="cmob-card-head">
            <span className="cmob-card-x" onClick={() => setConfigOpen(false)}>
              {Icon.close}
            </span>
            <div className="cmob-card-title">Configurações</div>
          </header>

          <div className="cmob-card-body">
            {/* Funil padrão */}
            <div className="cmob-cfg-group">
              <div className="cmob-cfg-lb">Funil padrão</div>
              <div className="cmob-cfg-sb">Abre a lista já filtrada por este funil.</div>
              <select value={funilFixo} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onFunilFixo(e.target.value)}>
                <option value="">Nenhum (todos os funis)</option>
                {funnels.map((f) => (
                  <option key={f.nome_funil} value={f.nome_funil}>
                    {f.nome_funil}
                  </option>
                ))}
              </select>
            </div>

            {/* Etapa-meta */}
            <div className="cmob-cfg-group">
              <div className="cmob-cfg-lb">Etapa de meta alcançada {Icon.trophy}</div>
              <div className="cmob-cfg-sb">Leads nesta etapa ganham um troféu na lista e na conversa.</div>
              <select value={etapaMeta} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onEtapaMeta(e.target.value)}>
                <option value="">Nenhuma</option>
                {todasEtapas.map((nome) => (
                  <option key={nome} value={nome}>
                    {nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Etiquetas coloridas */}
            <div className="cmob-cfg-row">
              <div className="cmob-cfg-info">
                <div className="cmob-cfg-lb">Etiquetas de etapa coloridas</div>
                <div className="cmob-cfg-sb">Cada etapa ganha uma cor própria.</div>
              </div>
              <button
                className={`cmob-switch${etiquetasCores ? " on" : ""}`}
                onClick={() => onEtiquetasCores(!etiquetasCores)}
                aria-label="Etiquetas coloridas"
              >
                <span className="cmob-switch-dot" />
              </button>
            </div>

            {/* Push real (Story 9): mensagens novas notificam este aparelho */}
            <div className="cmob-cfg-row">
              <div className="cmob-cfg-info">
                <div className="cmob-cfg-lb">Notificações de mensagens</div>
                <div className="cmob-cfg-sb">Avisa neste aparelho quando chegar mensagem nova, mesmo com o app fechado.</div>
              </div>
              <button
                className={`cmob-switch${pushOn ? " on" : ""}`}
                onClick={() => atualizarAssinatura(!pushOn, pushMeta)}
                disabled={pushBusy}
                aria-label="Notificações de mensagens"
              >
                <span className="cmob-switch-dot" />
              </button>
            </div>

            {/* Push de meta atingida 🏆 (Story 10): etapas marcadas como meta no
                dash (Configurar Etapa) disparam push quando um lead chega nelas */}
            <div className="cmob-cfg-row">
              <div className="cmob-cfg-info">
                <div className="cmob-cfg-lb">Notificar ao atingir a meta 🏆</div>
                <div className="cmob-cfg-sb">
                  Push quando um lead chega numa etapa marcada como meta (configura no dash, em Configurar Etapa).
                </div>
              </div>
              <button
                className={`cmob-switch${pushMeta ? " on" : ""}`}
                onClick={() => atualizarAssinatura(pushOn, !pushMeta)}
                disabled={pushBusy}
                aria-label="Notificar ao atingir a meta"
              >
                <span className="cmob-switch-dot" />
              </button>
            </div>
            {pushMsg && <div className="cmob-cfg-note">{pushMsg}</div>}

            {/* Sair da conta (2 toques) */}
            <button className={`cmob-sair${sairConfirm ? " confirm" : ""}`} onClick={sair}>
              {sairConfirm ? "Toque de novo para confirmar" : "Sair da conta"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ErrorBoundary local: qualquer exceção de render no mobile virava tela branca
   definitiva (sem boundary o React desmonta a árvore toda — visto em produção
   com o crash da busca). Fallback dá saída pro usuário sem F5. Só do mobile;
   não toca no desktop /chat. */
interface CMobErrorBoundaryState {
  erro: boolean;
}
class ChatMobileErrorBoundary extends React.Component<{ children: React.ReactNode }, CMobErrorBoundaryState> {
  state: CMobErrorBoundaryState = { erro: false };
  static getDerivedStateFromError(): CMobErrorBoundaryState {
    return { erro: true };
  }
  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error("[ChatMobile] erro de render capturado pelo boundary:", error);
  }
  render() {
    if (this.state.erro) {
      return (
        <div
          style={{
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: 24,
            textAlign: "center",
            background: "#f0f2f5",
            fontFamily: "inherit",
          }}
        >
          <div style={{ fontSize: 40 }}>😕</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#111b21" }}>Algo deu errado por aqui</div>
          <div style={{ fontSize: 14, color: "#667781" }}>Toque no botão abaixo para recarregar as conversas.</div>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#008069",
              color: "#fff",
              border: "none",
              borderRadius: 24,
              padding: "12px 28px",
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Recarregar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ChatMobileComBoundary: React.FC = () => (
  <ChatMobileErrorBoundary>
    <ChatMobile />
  </ChatMobileErrorBoundary>
);

export default ChatMobileComBoundary;
